import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import {
  formatEquation,
  applyOperation as applyMathOperation,
  substitute,
  makeEquation,
  type Equation,
  type AlgebraOperation,
} from '../shared/algebra.ts';
import type { LessonPackage } from '../shared/lessonPackage.ts';
import { LessonLibrary, WorkshopArtwork } from './components/LessonLibrary.tsx';
import {
  packageTeachingFrame,
  packageWrittenPlan,
} from './lib/packageTeaching.ts';
import type {
  ChoiceOption,
  LearningSession,
  LessonContent,
  SessionAction,
  StageId,
} from '../shared/types.ts';
import { Icon } from './components/Icon.tsx';
import {
  AnimatedBoard,
  useReducedMotion,
} from './components/AnimatedBoard.tsx';
import { EquationWorkshop } from './components/EquationWorkshop.tsx';
import { WrittenBoard } from './components/WrittenBoard.tsx';
import { SketchPad } from './components/SketchPad.tsx';
import {
  operationNarration,
  candidateNarration,
  speakMath,
  thinkingTimeRequest,
  shouldOfferHelp,
} from '../shared/tutorGuidance.ts';
import { OperationDemonstration } from './components/OperationDemonstration.tsx';
import { lessonFrame, replyFocus, type SceneFrame } from './lib/lessonScene.ts';
import { narratedOperation } from './lib/narratedOperation.ts';
import { buildAlgebraTeachingPlan } from './lib/algebraTeachingPlan.ts';
import {
  detectSpokenMath,
  parseLinearEquationDraft,
} from '../shared/spokenMath.ts';
import { TutorAvatar } from './components/TutorAvatar.tsx';
import { LearningRecord } from './components/LearningRecord.tsx';
import {
  WorkshopProgress,
  WorkshopReview,
  WorkshopExit,
} from './components/WorkshopProgress.tsx';
import { progressViewFor } from './lib/workshopProgress.ts';

import type { AudioManifest, Exposure } from './components/NarrationPlayer.tsx';
import { api, ApiError } from './lib/api.ts';
import { commandId } from './lib/commandId.ts';
import { interpretOperation } from './lib/interpret.ts';
import { useRecorder } from './lib/useRecorder.ts';
import { useTutorVoice } from './lib/useTutorVoice.ts';

import { getStageLesson } from '../shared/lesson.ts';
import {
  storyFor,
  storyFacts,
  storyModelNarration,
} from './lib/storyPresentation.ts';
import { useTutorNotebook } from './lib/useTutorNotebook.ts';
import type { PageDraft, TutorPage } from './lib/tutorNotebook.ts';
import { TutorNotebook } from './components/TutorNotebook.tsx';
import { diagnostic, downloadDiagnostics } from './lib/diagnostics.ts';
import {
  viewNarration,
  isolated,
  historyLabel,
  demonstrationProgress,
  type BoardStyle,
} from './lib/workshopFlow.ts';

const stages: StageId[] = ['concept', 'application', 'transfer'];
const names = {
  concept: 'Understand',
  application: 'Apply',
  transfer: 'Try a new situation',
};
const preference = (key: string, fallback: string) => {
  try {
    return localStorage.getItem(`enactry.preference.${key}`) ?? fallback;
  } catch {
    return fallback;
  }
};
const savePreference = (key: string, value: string) => {
  try {
    localStorage.setItem(`enactry.preference.${key}`, value);
  } catch {
    /* Controls still work when storage is unavailable. */
  }
};
const hasModel = (p: LearningSession['stages'][StageId]) =>
  !!(
    p.unknownAnswer?.correct &&
    (p.modelAnswer?.correct || p.modelEquation?.correct)
  );
function parsedDraft(text: string) {
  try {
    return parseLinearEquationDraft(text);
  } catch {
    return undefined;
  }
}
type Mode = 'welcome' | 'teach' | 'practice';
type Panel = 'model' | 'solve' | 'check';
type Tray = 'ask' | 'transcript' | 'history' | 'settings';
type Boot = {
  lesson: LessonPackage;
  digest: string;
  session: LearningSession;
  manifest?: AudioManifest;
  capabilities?: { tts: { available: boolean }; stt: { available: boolean } };
};
import { lessonSessionKey } from './lib/lessonSelection.ts';

async function bootstrap(): Promise<Boot> {
  const params = new URLSearchParams(window.location.search);
  const requestedId = params.get('lesson');
  const requestedVersion = params.get('version');
  const sessionKey = lessonSessionKey(requestedId, requestedVersion);
  let session: LearningSession | undefined;
  const saved = localStorage.getItem(sessionKey);
  if (saved) {
    try {
      session = await api<LearningSession>(`/api/sessions/${saved}`);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
    }
  }
  session ??= await api<LearningSession>('/api/sessions', {
    method: 'POST',
    body: JSON.stringify({
      ...(requestedId ? { lessonId: requestedId } : {}),
      ...(requestedVersion ? { lessonVersion: requestedVersion } : {}),
    }),
  });
  const resolved = await api<{ package: LessonPackage; digest: string }>(
    `/api/sessions/${session.id}/lesson`,
  );
  if (
    (requestedId && resolved.package.id !== requestedId) ||
    (requestedVersion && resolved.package.version !== requestedVersion)
  )
    throw new Error(
      'This saved workshop belongs to a different lesson version. Open its original workshop from the lesson shelf.',
    );
  localStorage.setItem(sessionKey, session.id);
  const [manifest, capabilities] = await Promise.all([
    fetch('/audio/manifest.json')
      .then((r) => (r.ok ? (r.json() as Promise<AudioManifest>) : undefined))
      .catch(() => undefined),
    api<Boot['capabilities']>('/api/capabilities').catch(() => undefined),
  ]);
  return {
    lesson: resolved.package,
    digest: resolved.digest,
    session,
    manifest,
    capabilities,
  };
}

function Choices({
  options,
  selected,
  onChoose,
  disabled,
}: {
  options: ChoiceOption[];
  selected?: string;
  onChoose: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="choices">
      {options.map((o, i) => (
        <button
          key={o.id}
          className={`choice ${selected === o.id ? 'selected' : ''}`}
          aria-pressed={selected === o.id}
          disabled={disabled}
          onClick={() => onChoose(o.id)}
        >
          <span className="choice-letter">{String.fromCharCode(65 + i)}</span>
          <span>{o.label}</span>
          {selected === o.id && <Icon name="check" />}
        </button>
      ))}
    </div>
  );
}
const clock = (ms: number) =>
  `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;
function substitutionText(
  equation: Parameters<typeof substitute>[0],
  value: string,
) {
  const result = substitute(equation, value);
  return `${result.left} ${result.equal ? '=' : '≠'} ${result.right}`;
}

export default function App() {
  return new URLSearchParams(window.location.search).get('library') === '1' ? (
    <LessonLibrary />
  ) : (
    <WorkshopApp />
  );
}
function WorkshopApp() {
  const [boot, setBoot] = useState<Boot>();
  const teachingBeats = boot?.lesson.teaching.beats ?? [];
  const TEACHING_VERSION = boot?.lesson.teaching.version ?? '';
  const [session, setSession] = useState<LearningSession>();
  const notebook = useTutorNotebook(session?.id);
  const [deletedNote, setDeletedNote] = useState<TutorPage>();
  const playingPageId = useRef<string | undefined>(undefined);
  const interruptedSpeech = useRef<
    { source: unknown; playing: boolean; generation: number } | undefined
  >(undefined);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(0);
  const [mode, setMode] = useState<Mode>('welcome');
  const [panel, setPanel] = useState<Panel>('solve');
  const [beatIndex, setBeatIndex] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [voiceReplies, setVoiceReplies] = useState(true);
  const [tray, setTray] = useState<Tray>('ask');
  const [trayOpen, setTrayOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [operation, setOperation] =
    useState<AlgebraOperation['kind']>('subtract');
  const [amount, setAmount] = useState('');
  const [candidate, setCandidate] = useState('');
  const [unit, setUnit] = useState('');
  const [question, setQuestion] = useState('');
  const [drafts, setDrafts] = useState<Partial<Record<StageId, string>>>({});
  const [selectedTerm, setSelectedTerm] = useState('');
  const [inlineAnswer, setInlineAnswer] = useState(true);
  const [alwaysConversation, setAlwaysConversation] = useState(
    () => preference('conversation', 'false') === 'true',
  );
  const [conversationVisible, setConversationVisible] = useState(
    () => preference('conversation', 'false') === 'true',
  );
  const [boardStyle, setBoardStyle] = useState<'illustration' | 'written'>(
    () =>
      preference('board', 'illustration') === 'written'
        ? 'written'
        : 'illustration',
  );
  const [showWorking, setShowWorking] = useState(false);
  const [demonstrating, setDemonstrating] = useState(false);
  const [customProblem, setCustomProblem] = useState(false);
  const [problemValues, setProblemValues] = useState({
    startingFee: '6',
    hourlyRate: '3',
    total: '18',
  });
  const [selectedVoice, setSelectedVoice] = useState(() =>
    preference('voice', 'af_sarah'),
  );
  const [checkIns, setCheckIns] = useState(
    () => preference('check-ins', 'true') === 'true',
  );
  const [checkInSeconds, setCheckInSeconds] = useState(
    () => Number(preference('check-in-seconds', '60')) || 60,
  );
  const [helpOffered, setHelpOffered] = useState(false);
  const [demonstration, setDemonstration] = useState<{
    equation: Equation;
    operation?: AlgebraOperation;
    text: string;
  }>();
  const [workshopAudio, setWorkshopAudio] = useState<
    AudioManifest & { voice: string }
  >();
  const idleState = useRef({
    lastActivity: Date.now(),
    snoozedUntil: 0,
    offered: false,
  });
  const checkInTick = useRef<() => void>(() => {});
  const beatTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const tutorialSequence = useRef<
    { firstUrl: string; second: AudioManifest['segments'][number] } | undefined
  >(undefined);
  const pendingQuestion = useRef('');
  const extraContext = useRef('');
  function markActivity() {
    idleState.current.lastActivity = Date.now();
    idleState.current.offered = false;
    setHelpOffered(false);
  }
  const [spokenProposal, setSpokenProposal] = useState<AlgebraOperation>();
  const reducedMotion = useReducedMotion();
  const tutorAbort = useRef<AbortController | undefined>(undefined);
  const [reply, setReply] = useState<{
    text: string;
    provider: string;
    revision: number;
  }>();
  const [tutorBusy, setTutorBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const initial = useRef<Promise<Boot> | undefined>(undefined);
  const sessionRef = useRef<LearningSession | undefined>(undefined);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const replyGeneration = useRef(0);
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const trayRef = useRef<HTMLDialogElement>(null);
  const update = useCallback((next: LearningSession) => {
    sessionRef.current = next;
    setSession(next);
  }, []);
  useEffect(() => {
    let active = true;
    initial.current ??= bootstrap();
    initial.current
      .then((data) => {
        if (!active) return;
        setBoot(data);
        if (!sessionRef.current) {
          update(data.session);
          const completed =
            data.session.teaching?.lessonVersion ===
              data.lesson.teaching.version && data.session.teaching.completed;
          setMode(
            completed ||
              data.session.activeStage !== 'concept' ||
              Object.values(data.session.stages).some((p) =>
                p.attempts.some((a) => a.kind !== 'hint' && a.kind !== 'tutor'),
              )
              ? 'practice'
              : 'welcome',
          );
          setBeatIndex(
            Math.max(
              0,
              data.lesson.teaching.beats.findIndex(
                (b) => b.id === data.session.teaching?.lastBeatId,
              ),
            ),
          );
          setPanel(
            data.session.activeStage === 'concept' ||
              hasModel(data.session.stages[data.session.activeStage])
              ? 'solve'
              : 'model',
          );
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [update]);
  useEffect(() => {
    void fetch('/audio/workshop-manifest.json')
      .then((r) => (r.ok ? r.json() : undefined))
      .then((data) => {
        if (data) setWorkshopAudio(data);
      })
      .catch(() => {});
    const timer = setInterval(() => checkInTick.current(), 1000);
    return () => {
      clearInterval(timer);
      clearTimeout(beatTimer.current);
    };
  }, []);
  function enqueue<T>(work: () => Promise<T>): Promise<T | undefined> {
    setBusy((n) => n + 1);
    const job = queue.current
      .then(work)
      .catch(async (err) => {
        if (err instanceof Error && err.name === 'AbortError') return undefined;
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Your draft is still here.',
        );
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          sessionRef.current
        ) {
          update(
            await api<LearningSession>(
              `/api/sessions/${sessionRef.current.id}`,
            ),
          );
          setError(
            'This lesson changed in another window. We loaded the saved version. Check it before trying again.',
          );
        }
        return undefined;
      })
      .finally(() => setBusy((n) => Math.max(0, n - 1)));
    queue.current = job.catch(() => undefined);
    return job;
  }
  function send(action: SessionAction, quiet = false) {
    if (!quiet) {
      setDemonstration(undefined);
      setDemonstrating(false);
      tutorialSequence.current = undefined;
      setSpokenProposal(undefined);
      interrupt();
      setError('');
      setStatus('');
    }
    return enqueue(async () => {
      const current = sessionRef.current;
      if (!current) throw new Error('Your lesson is still loading.');
      const next = await api<LearningSession>(
        `/api/sessions/${current.id}/actions`,
        {
          method: 'POST',
          body: JSON.stringify({
            expectedRevision: current.revision,
            commandId: commandId(),
            action,
          }),
        },
      );
      update(next);
      return next;
    });
  }
  function exposure(event: Exposure) {
    const stageId = teachingBeats.some((b) => b.audioId === event.audioId)
      ? 'concept'
      : stages.find(
          (id) => boot?.lesson.stages[id].audioIds.intro === event.audioId,
        );
    void send({ type: 'playback', stageId, ...event }, true);
  }
  const voice = useTutorVoice({
    onExposure: exposure,
    onReplyEnd: (url) => {
      const tutorial = tutorialSequence.current;
      if (tutorial?.firstUrl === url) {
        tutorialSequence.current = undefined;
        voice.reply(tutorial.second.url, tutorial.second.text, tutorial.second);
      }
    },
    onLessonEnd: (id) => {
      const index = teachingBeats.findIndex((b) => b.audioId === id);
      if (mode !== 'teach' || index !== beatIndex) return;
      if (index === teachingBeats.length - 1) void finishTeaching(false);
      else if (autoAdvance && !trayOpen && !recordOpen)
        beatTimer.current = setTimeout(() => goBeat(index + 1, true), 1600);
    },
  });
  function interrupt() {
    if (voice.playing) diagnostic('pause');
    clearTimeout(beatTimer.current);
    replyGeneration.current++;
    tutorAbort.current?.abort();
    setTutorBusy(false);
    setAudioBusy(false);
    voice.pause();
  }
  function noteDraft(
    text: string,
    title = 'Let’s think it through',
    context?: Partial<PageDraft>,
  ): PageDraft {
    const s = sessionRef.current!;
    return {
      title,
      text,
      stageId: s.activeStage,
      panel,
      equation:
        panel === 'check'
          ? getStageLesson(s, s.activeStage, boot?.lesson).initialEquation
          : mode === 'teach'
            ? teachingBeats[beatIndex].equation
            : s.stages[s.activeStage].equation,
      ...context,
    };
  }
  function closeNotebook() {
    interrupt();
    notebook.setOpen(false);
    setDemonstrating(false);
  }
  function selectNote(id: string) {
    interrupt();
    voice.clear();
    playingPageId.current = undefined;
    notebook.select(id);
    setMode('practice');
    diagnostic('note-open');
  }
  async function playNote(page: TutorPage) {
    interrupt();
    notebook.select(page.id);
    setMode('practice');
    playingPageId.current = page.id;
    diagnostic('replay');
    const cached = workshopAudio?.segments.find(
      (s) => workshopAudio.voice === selectedVoice && s.text === page.text,
    );
    if (cached) {
      notebook.patch(page.id, { audio: { ...cached, createdAt: Date.now() } });
      diagnostic('tts', 0, 'cached');
      voice.reply(cached.url, page.text, {
        ...cached,
        voice: workshopAudio?.voice,
      });
      return;
    }
    // Rebind private audio through the server cache. Its file may have been
    // pruned before a saved page's browser metadata expires.
    await speakReply(page.text, ++replyGeneration.current, page.id);
  }
  async function requestHint() {
    const current = sessionRef.current;
    if (!current) return;
    const next = await send({ type: 'hint' });
    if (!next) return;
    const p = next.stages[next.activeStage],
      eq =
        panel === 'check'
          ? getStageLesson(next, next.activeStage, boot?.lesson).initialEquation
          : p.equation;
    const level = Math.min(p.hintsUsed, 3);
    const text =
      panel === 'check'
        ? `Put your value in place of every x in the original equation. Multiply first, then add. Compare the two totals. What do equal totals tell you?`
        : panel === 'model'
          ? `First identify the repeated quantity and the fixed quantity. Which number belongs next to x? Explain that connection before you check your model.`
          : isolated(p)
            ? `You have isolated x. Check that value in the original equation. A matching total on both sides is evidence that your value works.`
            : level === 1
              ? `Look at ${speakMath(formatEquation(eq))}. Our aim is to leave x by itself. Which term could you undo first, while making the same change to both sides?`
              : eq.left.constant !== '0'
                ? `The extra ${eq.left.constant} is added to the x term. Subtracting ${eq.left.constant} undoes that addition. Make that same subtraction on the other side to keep equality. What remains on each side?`
                : `The coefficient ${eq.left.x} multiplies x. Dividing both entire sides by that nonzero coefficient reverses the multiplication. Why must you change both sides?`;
    setMode('practice');
    void sayGuidance(
      text,
      undefined,
      noteDraft(text, `Hint ${level}`, { equation: eq }),
    );
  }
  function openTray(next: Tray) {
    interrupt();
    setTray(next);
    setTrayOpen(true);
    markActivity();
  }
  function resumeTeaching() {
    interrupt();
    voice.resumeLesson();
  }
  function resumeCurrent() {
    interrupt();
    showBoundNote();
    diagnostic('play');
    void voice.play();
  }
  function showBoundNote() {
    const page = notebook.pages.find((p) => p.id === playingPageId.current);
    if (
      page &&
      voice.source?.kind === 'reply' &&
      voice.source.text === page.text
    ) {
      notebook.select(page.id);
      setMode('practice');
    }
  }
  function replayVoice() {
    interrupt();
    showBoundNote();
    diagnostic('replay');
    voice.replay();
  }
  const recorder = useRecorder({
    onSpeechStart: () => {
      interruptedSpeech.current = {
        source: voice.source,
        playing: voice.playing,
        generation: replyGeneration.current + 1,
      };
      diagnostic('speech-start');
      if (tutorBusy || audioBusy)
        extraContext.current = pendingQuestion.current;
      interrupt();
      markActivity();
      setStatus('');
    },
    onComplete: async (wav, signal) => {
      const started = performance.now();
      const result = await api<{
        text: string;
        provider?: string;
        timingMs?: { queue: number; processing: number };
      }>('/api/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': 'audio/wav' },
        body: wav,
        signal,
      }).catch((err: unknown) => {
        diagnostic(
          'stt',
          performance.now() - started,
          signal.aborted ? 'aborted' : 'error',
        );
        throw err;
      });
      diagnostic(
        'stt',
        performance.now() - started,
        result.text ? 'ok' : 'empty',
        result,
      );
      if (signal.aborted) return;
      setQuestion(result.text);
      if (!result.text) {
        setStatus('No words were recognized. Try again or type.');
        const saved = interruptedSpeech.current;
        if (
          saved?.playing &&
          saved.source === voice.source &&
          saved.generation === replyGeneration.current
        )
          void voice.play();
      } else await handleUtterance(result.text, signal);
    },
  });
  function openProgress() {
    interrupt();
    recorder.cancelTurn();
    setError('');
    setTrayOpen(false);
    setProgressOpen(true);
  }
  async function leaveWorkshop(finish: boolean) {
    const current = sessionRef.current;
    if (current)
      savePreference(
        `resume.${current.id}`,
        JSON.stringify({ mode, panel, beatIndex, boardStyle }),
      );
    interrupt();
    await recorder.stop();
    voice.clear();
    notebook.setOpen(false);
    setTrayOpen(false);
    const next = await send(
      finish
        ? {
            type: 'finishWorkshop',
            assessmentVersion: boot!.lesson.assessment.version,
          }
        : { type: 'exitWorkshop' },
    );
    if (next) {
      setProgressOpen(false);
      setRecordOpen(false);
    }
  }
  async function resumeWorkshop() {
    const next = await send({ type: 'resumeWorkshop' });
    if (next) {
      let saved: Record<string, unknown> = {};
      try {
        saved = JSON.parse(preference(`resume.${next.id}`, '{}')) ?? {};
      } catch {
        /* Fall back to the saved activity. */
      }
      const fallback =
        next.teaching?.viewedBeatIds.length ||
        Object.values(next.stages).some((p) => p.attempts.length)
          ? 'practice'
          : 'welcome';
      setMode(
        saved.mode === 'practice' ||
          saved.mode === 'teach' ||
          saved.mode === 'welcome'
          ? saved.mode
          : fallback,
      );
      setPanel(
        saved.panel === 'solve' ||
          saved.panel === 'check' ||
          saved.panel === 'model'
          ? saved.panel
          : next.activeStage === 'concept' ||
              hasModel(next.stages[next.activeStage])
            ? 'solve'
            : 'model',
      );
      if (
        Number.isInteger(saved.beatIndex) &&
        Number(saved.beatIndex) >= 0 &&
        Number(saved.beatIndex) < teachingBeats.length
      )
        setBeatIndex(Number(saved.beatIndex));
      if (saved.boardStyle === 'written' || saved.boardStyle === 'illustration')
        setBoardStyle(saved.boardStyle);
    }
  }
  async function handleUtterance(text: string, signal?: AbortSignal) {
    if (signal?.aborted) return;
    const current = sessionRef.current;
    if (!current) return;
    const command = text.trim().toLowerCase().replace(/[.!?]/g, '');
    if (
      /^(?:please )?(?:finish(?: the)? workshop|finish and exit|i(?:'m| am) done|show (?:my )?progress)$/.test(
        command,
      )
    ) {
      openProgress();
      return;
    }
    if (/^(?:please )?save and exit(?: for now)?$/.test(command)) {
      await leaveWorkshop(false);
      return;
    }
    if (progressOpen) {
      setStatus(
        'Your progress review is open. You can save and exit, or return to your activity.',
      );
      return;
    }

    const noteCommand = command.match(
      /^(?:please )?(?:open|show|revisit|replay|explain|read)(?: the| my| tutor)? (?:note|page)(?: number)? (\d+)$/,
    );
    if (noteCommand) {
      const page = notebook.pages[Number(noteCommand[1]) - 1];
      if (page) await playNote(page);
      else
        setStatus(
          'That page is not in your tutor notebook. Open the notebook to choose a page.',
        );
      return;
    }
    if (
      /^(?:please )?(?:give me |can i have |i need |i want )?(?:a |another |more )?hint(?:s)?(?: please)?$/.test(
        command,
      )
    ) {
      await requestHint();
      return;
    }
    if (
      /^(?:open|show)(?: the| my| tutor)? (?:tutor )?notebook$/.test(command)
    ) {
      const page = notebook.active ?? notebook.pages.at(-1);
      if (page) selectNote(page.id);
      else
        setStatus(
          'Ask for an explanation or hint to start your tutor notebook.',
        );
      return;
    }
    const previousContext = extraContext.current;
    extraContext.current = '';
    markActivity();
    const thinkingTime = thinkingTimeRequest(text);
    if (
      current.activeStage === 'transfer' &&
      /\b(?:another|new|different) (?:one|problem|situation)\b/.test(command) &&
      !/\b(?:not|don't|no)\b/.test(command)
    ) {
      await newProblem();
      return;
    }
    if (thinkingTime) {
      interrupt();
      idleState.current.snoozedUntil = Date.now() + thinkingTime;
      setStatus(
        `Take your time. I’ll wait ${thinkingTime >= 60000 ? `${Math.round(thinkingTime / 60000)} minute${thinkingTime >= 120000 ? 's' : ''}` : `${thinkingTime / 1000} seconds`} before offering help.`,
      );
      return;
    }
    if (
      /^(?:please )?(?:no check ins|stop checking in|don'?t interrupt me|leave me to think)$/.test(
        command,
      )
    ) {
      setCheckIns(false);
      savePreference('check-ins', 'false');
      interrupt();
      setStatus('Take your time. Ask whenever you need me.');
      return;
    }
    if (/^(?:show|open) (?:my )?(?:notes|sketch ?pad|working)$/.test(command)) {
      setShowWorking(true);
      return;
    }
    if (
      /^(?:write it out|show the written (?:method|steps)|use the pencil)$/.test(
        command,
      )
    ) {
      setBoardStyle('written');
      savePreference('board', 'written');
      void explainCurrent();
      return;
    }
    if (/^(pause|stop talking|wait)( please)?$/.test(command)) {
      interrupt();
      return;
    }
    if (/^(resume|continue teaching|keep going)( please)?$/.test(command)) {
      resumeCurrent();
      return;
    }
    if (
      /^(return to (?:the )?lesson|resume (?:the )?lesson)( please)?$/.test(
        command,
      )
    ) {
      resumeTeaching();
      return;
    }
    if (/^(repeat|say that again|replay)( please)?$/.test(command)) {
      replayVoice();
      return;
    }
    if (/^(teach me|show me how)( please)?$/.test(command)) {
      void explainCurrent();
      return;
    }
    if (/^(next part|next step)( please)?$/.test(command) && mode === 'teach') {
      goBeat(Math.min(teachingBeats.length - 1, beatIndex + 1), true);
      return;
    }
    const activeLesson = getStageLesson(
      current,
      current.activeStage,
      boot?.lesson,
    );
    const unknownSpeech = command.match(
      /^(?:i think )?(?:x|the unknown) (?:means|is|stands for|represents) (.+)$/,
    )?.[1];
    const normalizeMeaning = (value: string) =>
      value
        .toLowerCase()
        .replace(/^(?:the )?(?:number of )?/, '')
        .replace(/ rented$/, '')
        .trim();
    const spokenUnknown = unknownSpeech
      ? activeLesson.unknownOptions.find(
          (o) => normalizeMeaning(o.label) === normalizeMeaning(unknownSpeech),
        )
      : undefined;
    if (
      mode === 'practice' &&
      current.activeStage !== 'concept' &&
      spokenUnknown
    ) {
      const next = await send({
        type: 'unknownAnswer',
        choiceId: spokenUnknown.id,
      });
      if (next) {
        setStatus(
          `Recorded: x means ${spokenUnknown.label.toLowerCase()}. Check how that meaning fits the story.`,
        );
        setPanel('model');
      }
      return;
    }
    if (
      /^(check my answer|check my value)( please)?$/.test(command) &&
      mode === 'practice' &&
      panel === 'check' &&
      candidate.trim()
    ) {
      await submitCandidate();
      return;
    }
    if (
      /^(check my model|check my equation)( please)?$/.test(command) &&
      mode === 'practice' &&
      current.activeStage !== 'concept'
    ) {
      await checkModel();
      return;
    }
    if (
      /^(apply (that|this)( operation)?|yes apply it|confirm the move)$/.test(
        command,
      ) &&
      spokenProposal
    ) {
      const next = await commitOperation(spokenProposal);
      if (next) {
        setPanel('solve');
        setSpokenProposal(undefined);
        setQuestion('');
        setStatus('Applied to both entire sides.');
      }
      return;
    }
    const math = detectSpokenMath(
      text,
      activeLesson.unitOptions.map((option) => option.label),
    );
    if (
      math.kind === 'equation' &&
      mode === 'practice' &&
      panel === 'check' &&
      math.equation.left.x === '1' &&
      math.equation.left.constant === '0' &&
      math.equation.right.x === '0'
    ) {
      setCandidate(math.equation.right.constant);
      setStatus(
        `I heard x = ${math.equation.right.constant}. Check the value and units, then say “check my answer.”`,
      );
      return;
    }
    if (
      math.kind === 'equation' &&
      mode === 'practice' &&
      current.activeStage !== 'concept'
    ) {
      if (panel === 'solve') {
        await acceptWrittenEquation(math.draftEquation);
        return;
      }
      setDrafts((d) => ({ ...d, [current.activeStage]: math.draftEquation }));
      setPanel('model');
      setSelectedTerm('');
      setReply(undefined);
      setStatus(
        `I heard: ${math.draftEquation}. Edit any part, then say “check my model.”`,
      );
      return;
    }
    const move = interpretOperation(text);
    if (move && mode === 'practice') {
      setOperation(move.kind);
      setAmount(move.value);
      setSpokenProposal(move);
      setStatus(
        `Proposed: ${move.kind} ${move.value} on both sides. Say “apply that” to confirm.`,
      );
      return;
    }
    if (math.kind === 'value' && mode === 'practice' && panel === 'check') {
      setCandidate(math.value);
      const spokenUnit = activeLesson.unitOptions.find(
        (o) =>
          o.label.toLowerCase().replace(/s$/, '') ===
          math.unit?.toLowerCase().trim().replace(/s$/, ''),
      );
      if (spokenUnit) setUnit(spokenUnit.id);
      setStatus(
        `I heard x = ${math.value}. Check the value and units before submitting.`,
      );
      return;
    }
    await askTutor(
      undefined,
      previousContext
        ? `${previousContext}\nAdditional detail: ${text}`.slice(-1900)
        : text,
      signal,
    );
  }
  async function checkModel() {
    const current = sessionRef.current;
    if (!current || current.activeStage === 'concept') return;
    const equation =
      drafts[current.activeStage] ??
      current.stages[current.activeStage].modelEquation?.draftEquation ??
      '';
    if (!equation.trim()) {
      setStatus('Build or say your equation first.');
      return;
    }
    const request = send({ type: 'submitModelEquation', equation });
    const generation = replyGeneration.current;
    const next = await request;
    if (next && generation === replyGeneration.current) {
      const feedback =
        next.stages[current.activeStage].modelEquation?.feedback ??
        'Your model was checked.';
      setStatus(feedback);
      void sayGuidance(feedback);
    }
  }
  useEffect(() => {
    if (trayOpen) trayRef.current?.showModal();
    else trayRef.current?.close();
  }, [trayOpen]);
  useEffect(() => {
    const main = document.getElementById('lesson-main');
    if (main) main.scrollTop = 0;
  }, [mode, panel, session?.activeStage]);
  function goBeat(index: number, autoplay: boolean) {
    interrupt();
    setSpokenProposal(undefined);
    void recorder.cancelTurn();
    if (sessionRef.current?.activeStage !== 'concept') {
      void send({ type: 'setStage', stageId: 'concept' }, true);
    }
    setMode('teach');
    setBeatIndex(index);
    setReply(undefined);
    setStatus('');
    const beat = teachingBeats[index];
    void send(
      {
        type: 'teachingProgress',
        lessonVersion: TEACHING_VERSION,
        beatId: beat.id,
        event: 'viewed',
      },
      true,
    );
    voice.lesson(
      boot?.manifest?.segments.find((s) => s.id === beat.audioId),
      autoplay,
    );
  }
  async function finishTeaching(enterPractice: boolean) {
    if (enterPractice) recorder.cancelTurn();
    const allVisited = teachingBeats.every((b) =>
      sessionRef.current?.teaching?.viewedBeatIds.includes(b.id),
    );
    if (allVisited)
      await send(
        {
          type: 'teachingProgress',
          lessonVersion: TEACHING_VERSION,
          beatId: teachingBeats.at(-1)!.id,
          event: 'completed',
        },
        true,
      );
    if (enterPractice) {
      interrupt();
      voice.clear();
      setMode('practice');
      setPanel(
        sessionRef.current?.activeStage === 'concept' ||
          (sessionRef.current &&
            hasModel(sessionRef.current.stages[sessionRef.current.activeStage]))
          ? 'solve'
          : 'model',
      );
      setStatus('Your turn. Choose a change that preserves equality.');
    }
  }
  function skipTeaching() {
    interrupt();
    recorder.cancelTurn();
    voice.skip();
    void send(
      {
        type: 'teachingProgress',
        lessonVersion: TEACHING_VERSION,
        beatId: teachingBeats[beatIndex].id,
        event: 'skipped',
      },
      true,
    );
    voice.clear();
    setMode('practice');
    setPanel(
      session?.activeStage === 'concept' ||
        (session && hasModel(session.stages[session.activeStage]))
        ? 'solve'
        : 'model',
    );
    setStatus(
      'You can return to the worked lesson anytime. We will check your reasoning during practice.',
    );
  }
  async function changeStage(id: StageId) {
    notebook.setOpen(false);
    playingPageId.current = undefined;
    interrupt();
    recorder.cancelTurn();
    voice.clear();
    const request = send({ type: 'setStage', stageId: id });
    const generation = replyGeneration.current;
    const next = await request;
    if (!next) return;
    setMode('practice');
    setPanel(id === 'concept' || hasModel(next.stages[id]) ? 'solve' : 'model');
    setCandidate('');
    setUnit('');
    setAmount('');
    setQuestion('');
    setReply(undefined);
    setDemonstration(undefined);
    setShowWorking(false);
    markActivity();
    const nextPanel =
      id === 'concept' || hasModel(next.stages[id]) ? 'solve' : 'model';
    setDemonstrating(false);
    setCustomProblem(false);
    if (generation === replyGeneration.current)
      void sayGuidance(
        viewNarration(
          getStageLesson(next, id, boot?.lesson),
          next.stages[id],
          nextPanel,
          boardStyle,
        ),
      );
    requestAnimationFrame(() =>
      document.getElementById('lesson-main')?.focus(),
    );
  }
  async function newProblem(custom = false) {
    notebook.setOpen(false);
    playingPageId.current = undefined;
    interrupt();
    recorder.cancelTurn();
    voice.clear();
    const action: SessionAction = custom
      ? {
          type: 'newTransferProblem',
          mode: 'custom',
          problem: {
            startingFee: Number(problemValues.startingFee),
            hourlyRate: Number(problemValues.hourlyRate),
            total: Number(problemValues.total),
          },
        }
      : { type: 'newTransferProblem', mode: 'next' };
    const request = send(action);
    const generation = replyGeneration.current;
    const next = await request;
    if (!next) return;
    setMode('practice');
    setPanel('model');
    setShowWorking(false);
    setDemonstrating(false);
    setDemonstration(undefined);
    setCustomProblem(false);
    setCandidate('');
    setUnit('');
    setDrafts((d) => ({ ...d, transfer: '' }));
    setQuestion('');
    setReply(undefined);
    if (generation === replyGeneration.current)
      void sayGuidance(
        viewNarration(
          getStageLesson(next, 'transfer', boot?.lesson),
          next.stages.transfer,
          'model',
          boardStyle,
        ),
      );
  }
  async function askTutor(
    event?: FormEvent,
    spoken?: string,
    signal?: AbortSignal,
  ) {
    event?.preventDefault();
    const text = (spoken ?? question).trim();
    if (!text || signal?.aborted) return;
    const sourceNote = notebook.open ? notebook.active : undefined;
    const pageContext = sourceNote
      ? {
          equation: sourceNote.equation,
          stageId: sourceNote.stageId,
          panel: sourceNote.panel,
        }
      : undefined;
    const started = performance.now();
    pendingQuestion.current = text;
    tutorialSequence.current = undefined;
    const replyPlayback =
      voice.source?.kind === 'reply' &&
      reply &&
      reply.provider !== 'prepared-workshop'
        ? { tutorRevision: reply.revision, positionMs: voice.position }
        : undefined;
    interrupt();
    const abort = new AbortController();
    tutorAbort.current = abort;
    const abortTurn = () => abort.abort();
    signal?.addEventListener('abort', abortTurn, { once: true });
    setError('');
    setReply(undefined);
    setDemonstration(undefined);
    setTutorBusy(true);
    const generation = ++replyGeneration.current;
    const context = voice.snapshot();
    const teachingBeatId =
      mode === 'teach' ? teachingBeats[beatIndex].id : undefined;
    const result = await enqueue(async () => {
      if (abort.signal.aborted || generation !== replyGeneration.current)
        throw new DOMException('Interrupted', 'AbortError');
      const current = sessionRef.current!;
      const draftText =
        drafts[current.activeStage] ??
        current.stages[current.activeStage].modelEquation?.draftEquation ??
        '';
      const board = parsedDraft(draftText);
      const response = await api<{
        reply: string;
        provider: string;
        revision: number;
      }>(`/api/sessions/${current.id}/tutor`, {
        method: 'POST',
        signal: abort.signal,
        body: JSON.stringify({
          question: text,
          ...(sourceNote
            ? {
                notebookContext: {
                  text: sourceNote.text,
                  equation: formatEquation(sourceNote.equation),
                  pageId: sourceNote.id,
                },
              }
            : {}),
          practiceView: panel,
          ...(board
            ? {
                boardContext: {
                  draftEquation: board.draftEquation,
                  ...(selectedTerm && board.draftEquation.includes(selectedTerm)
                    ? { selectedTerm }
                    : {}),
                },
              }
            : {}),
          ...(replyPlayback ? { replyPlayback } : {}),
          ...(teachingBeatId ? { teachingBeatId } : {}),
          ...(!replyPlayback && context
            ? {
                playback: {
                  segmentId: context.segmentId,
                  positionMs: context.positionMs,
                },
              }
            : {}),
        }),
      });
      update(await api<LearningSession>(`/api/sessions/${current.id}`));
      if (generation === replyGeneration.current)
        setReply({
          text: response.reply,
          provider: response.provider,
          revision: response.revision,
        });
      return response;
    });
    signal?.removeEventListener('abort', abortTurn);
    if (generation === replyGeneration.current) setTutorBusy(false);
    if (!result)
      diagnostic(
        'tutor',
        performance.now() - started,
        abort.signal.aborted ? 'aborted' : 'error',
      );
    let page: TutorPage | undefined;
    if (result && generation === replyGeneration.current) {
      diagnostic('tutor', performance.now() - started, 'ok');
      page = notebook.add(
        noteDraft(result.reply, 'Let’s think it through', pageContext),
      );
      playingPageId.current = page?.id;
      setMode('practice');
    }
    if (
      result &&
      generation === replyGeneration.current &&
      voiceReplies &&
      boot?.capabilities?.tts.available
    )
      void speakReply(result.reply, generation, page?.id);
  }
  async function speakReply(
    text: string,
    generation = ++replyGeneration.current,
    pageId?: string,
  ) {
    const started = performance.now();
    voice.pause();
    setAudioBusy(true);
    try {
      const abort = new AbortController();
      tutorAbort.current = abort;
      const result = await api<{
        url: string;
        durationMs?: number;
        words?: { text: string; startMs: number; endMs: number }[];
        timingMethod?: string;
        provider?: string;
        timingMs?: { queue: number; processing: number };
      }>('/api/speech', {
        method: 'POST',
        body: JSON.stringify({ text, voice: selectedVoice }),
        signal: abort.signal,
      });
      if (generation === replyGeneration.current) {
        diagnostic('tts', performance.now() - started, 'ok', result);
        if (pageId) {
          notebook.patch(pageId, {
            audio: { ...result, createdAt: Date.now() },
          });
          playingPageId.current = pageId;
        }
        voice.reply(result.url, text, result);
      }
    } catch (err) {
      diagnostic(
        'tts',
        performance.now() - started,
        err instanceof Error && err.name === 'AbortError' ? 'aborted' : 'error',
      );
      if (generation === replyGeneration.current)
        setError(
          err instanceof Error
            ? err.message
            : 'Voice is unavailable. Your reply is still readable.',
        );
    } finally {
      if (generation === replyGeneration.current) setAudioBusy(false);
    }
  }
  async function sayGuidance(
    text: string,
    clipId?: string,
    pageDraft?: PageDraft,
  ) {
    if (!text.trim()) return;
    interrupt();
    tutorialSequence.current = undefined;
    const generation = ++replyGeneration.current;
    const page = pageDraft ? notebook.add(pageDraft) : undefined;
    playingPageId.current = page?.id;
    setReply({
      text,
      provider: 'prepared-workshop',
      revision: sessionRef.current?.revision ?? 0,
    });
    const cached = workshopAudio?.segments.find(
      (s) =>
        workshopAudio.voice === selectedVoice &&
        (clipId ? s.id === clipId : s.text === text),
    );
    if (!voiceReplies) return;
    if (cached) {
      if (page)
        notebook.patch(page.id, {
          audio: { ...cached, createdAt: Date.now() },
        });
      diagnostic('tts', 0, 'cached');
      voice.reply(cached.url, cached.text, {
        ...cached,
        voice: workshopAudio?.voice,
      });
    } else if (boot?.capabilities?.tts.available)
      await speakReply(text, generation, page?.id);
  }
  function startSketchTutorial(text: string) {
    const first = workshopAudio?.segments.find(
      (s) => s.id === 'sketch-onboarding-write',
    );
    const second = workshopAudio?.segments.find(
      (s) => s.id === 'sketch-onboarding-preview',
    );
    if (!first || !second) {
      void sayGuidance(text);
      return;
    }
    interrupt();
    setReply({
      text,
      provider: 'prepared-workshop',
      revision: sessionRef.current?.revision ?? 0,
    });
    if (voiceReplies) {
      tutorialSequence.current = { firstUrl: first.url, second };
      voice.reply(first.url, first.text, first);
    }
  }
  async function commitOperation(move: AlgebraOperation) {
    const current = sessionRef.current;
    if (!current) return;
    const request = send({ type: 'applyOperation', operation: move });
    const generation = replyGeneration.current;
    const next = await request;
    if (generation !== replyGeneration.current) return;
    if (next) {
      const applied = next.stages[next.activeStage].history.at(-1);
      if (!applied) return next;
      const text = operationNarration(applied.equation, applied.operation);
      setDemonstrating(true);
      setDemonstration({
        equation: applied.equation,
        operation: applied.operation,
        text,
      });
      setAmount('');
      setStatus(
        'Both entire sides changed. Listen or replay the written steps.',
      );
      void sayGuidance(
        text,
        undefined,
        noteDraft(text, 'A reversible step', {
          equation: applied.equation,
          operation: applied.operation,
        }),
      );
    }
    return next;
  }
  async function acceptWrittenEquation(text: string) {
    const current = sessionRef.current;
    if (!current) return;
    const parsed = parsedDraft(text);
    if (!parsed) {
      setError(
        'I could not read a linear equation yet. Check the preview and use numbers and x.',
      );
      return;
    }
    if (panel === 'model' && current.activeStage !== 'concept') {
      setDrafts((d) => ({ ...d, [current.activeStage]: parsed.draftEquation }));
      setStatus(
        'Your confirmed writing is on the model board. Check it when ready.',
      );
      return;
    }
    const request = send({
      type: 'submitWorkingEquation',
      equation: parsed.draftEquation,
    });
    const generation = replyGeneration.current;
    const next = await request;
    if (!next || generation !== replyGeneration.current) return;
    const line = next.stages[current.activeStage].workingEquations?.at(-1);
    if (
      panel === 'check' &&
      line?.equivalent &&
      parsed.equation.left.x === '1' &&
      parsed.equation.left.constant === '0' &&
      parsed.equation.right.x === '0'
    )
      setCandidate(parsed.equation.right.constant);
    const message = line?.equivalent
      ? 'Your written equation has the same solution. Tell me how you got there, then check your answer in the original.'
      : 'This written equation has a different solution. Compare it with your earlier correct work; we can find the change together.';
    setStatus(message);
    void sayGuidance(message);
  }
  async function submitCandidate() {
    const current = sessionRef.current;
    if (!current) return;
    const request = send({
      type: 'checkCandidate',
      value: candidate,
      ...(current.activeStage === 'concept' ? {} : { unit }),
    });
    const generation = replyGeneration.current;
    const next = await request;
    if (generation !== replyGeneration.current) return;
    if (next) {
      const p = next.stages[current.activeStage];
      const original = getStageLesson(
        next,
        current.activeStage,
        boot?.lesson,
      ).initialEquation;
      const message =
        candidateNarration(original, p.candidateCheck!.value) +
        (current.activeStage !== 'concept' && !p.unitAnswer?.correct
          ? ' Check the units too: what does x represent in the story?'
          : '');
      setDemonstration({ equation: original, text: message });
      setBoardStyle('written');
      setShowWorking(false);
      setStatus(message);
      void sayGuidance(
        message,
        undefined,
        noteDraft(message, 'Check the original equation', {
          equation: original,
          candidate: p.candidateCheck!.value,
        }),
      );
    }
  }
  async function explainCurrent(forceWritten = false) {
    const current = sessionRef.current;
    if (!current || !boot) return;
    interrupt();
    recorder.cancelTurn();
    voice.clear();
    if (forceWritten && boardStyle !== 'written') {
      setBoardStyle('written');
      savePreference('board', 'written');
    }
    const s = getStageLesson(current, current.activeStage, boot?.lesson),
      p = current.stages[current.activeStage];
    const eq =
      panel === 'model' || panel === 'check' ? s.initialEquation : p.equation;
    let move: AlgebraOperation | undefined;
    if (panel !== 'model' && panel !== 'check') {
      if (eq.left.constant !== '0')
        move = { kind: 'subtract', value: eq.left.constant };
      else if (eq.right.x === '0' && eq.left.x !== '0' && eq.left.x !== '1')
        move = { kind: 'divide', value: eq.left.x };
    }
    const text = move
      ? operationNarration(eq, move)
      : panel === 'model'
        ? storyModelNarration(s)
        : `Read the ${panel === 'check' ? 'original' : 'current'} relationship: ${speakMath(formatEquation(eq))}. Replace x with your proposed value. Work out each side separately, then compare. Equal results confirm the value.`;
    setMode('practice');
    setDemonstrating(true);
    setShowWorking(false);
    setDemonstration({ equation: eq, operation: move, text });
    void sayGuidance(
      text,
      undefined,
      noteDraft(text, 'Why this step works', { equation: eq, operation: move }),
    );
  }
  checkInTick.current = () => {
    if (
      shouldOfferHelp({
        ...idleState.current,
        now: Date.now(),
        interval: checkInSeconds * 1000,
        enabled: checkIns,
        available: recorder.enabled,
        occupied:
          mode !== 'practice' ||
          progressOpen ||
          (session?.workshop?.status !== undefined &&
            session.workshop.status !== 'active') ||
          busy > 0 ||
          tutorBusy ||
          audioBusy ||
          voice.playing ||
          trayOpen ||
          recordOpen ||
          recorder.status === 'recording' ||
          recorder.status === 'transcribing',
        visible: document.visibilityState === 'visible',
      })
    ) {
      idleState.current.offered = true;
      setHelpOffered(true);
      const clip = workshopAudio?.segments.find(
        (s) => s.id === 'sketch-onboarding-checkin',
      );
      const text =
        clip?.text ??
        'Would you like a hint, another explanation, or more time to think? You can say, give me two minutes.';
      void sayGuidance(text, clip?.id);
    }
  };
  if (!boot || !session)
    return (
      <main className="boot-screen">
        <span className="brand">
          <Icon name="spark" /> enactry
        </span>
        <h1>
          {error
            ? 'The studio could not open.'
            : 'Opening your algebra studio…'}
        </h1>
        <p>{error || 'Loading your saved lesson.'}</p>
        {error && (
          <button className="primary" onClick={() => location.reload()}>
            Try again
          </button>
        )}
      </main>
    );
  const progressView = progressViewFor(session, boot.lesson);
  if (
    session.workshop?.status === 'exited' ||
    session.workshop?.status === 'completed'
  )
    return (
      <>
        <WorkshopExit
          title={boot.lesson.title}
          view={progressView}
          finished={session.workshop.status === 'completed'}
          busy={busy > 0}
          error={error}
          onResume={() => void resumeWorkshop()}
          onRecord={() => setRecordOpen(true)}
        />
        <LearningRecord
          lesson={boot.lesson}
          session={session}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
        />
      </>
    );
  const stageId = session.activeStage,
    stage = getStageLesson(session, stageId, boot?.lesson),
    progress = session.stages[stageId];
  const story = storyFor(stage);
  const facts = storyFacts(stage);
  const beat = teachingBeats[beatIndex];
  const teaching = mode === 'teach',
    isConcept = stageId === 'concept';
  const locked = busy > 0;
  const proposal = interpretOperation(question),
    nextStage = stages[stages.indexOf(stageId) + 1];
  const secureMic =
    !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
  const micActive = recorder.enabled || recorder.status === 'requesting';
  const currentSegment =
    voice.source?.kind === 'lesson' ? voice.source.segment : undefined;
  const transcript =
    voice.source?.kind === 'reply'
      ? voice.source.text
      : teaching
        ? beat.narration
        : (currentSegment?.text ?? stage.prompt);
  const currentSentence = currentSegment?.checkpoints
    .filter((c) => c.startMs <= voice.position)
    .at(-1)?.text;
  const draftEquation =
    drafts[stageId] ?? progress.modelEquation?.draftEquation ?? '';
  const sceneSegment = boot.manifest?.segments.find(
    (s) => s.id === beat.audioId,
  );
  const lessonPosition =
    voice.source?.kind === 'lesson' && voice.source.segment.id === beat.audioId
      ? voice.position
      : voice.snapshot()?.segmentId === beat.audioId
        ? (voice.snapshot()?.positionMs ?? 0)
        : 0;
  const packageVisual = packageTeachingFrame(
    beat,
    sceneSegment,
    lessonPosition,
    undefined,
    reducedMotion,
  );
  const baseFrame = beat.visual
    ? packageVisual.frame
    : lessonFrame(beat.id, sceneSegment, lessonPosition, reducedMotion);
  const focusEquation =
    demonstration?.equation ??
    (panel === 'model'
      ? (parsedDraft(draftEquation)?.equation ?? progress.equation)
      : teaching
        ? beat.equation
        : panel === 'check'
          ? stage.initialEquation
          : progress.equation);
  const spokenFocus =
    voice.source?.kind === 'reply'
      ? replyFocus(
          voice.source.text,
          voice.position,
          voice.duration,
          focusEquation,
          voice.source.words,
        )
      : !teaching && currentSegment
        ? replyFocus(
            currentSegment.text,
            voice.position,
            currentSegment.durationMs,
            focusEquation,
            currentSegment.words,
          )
        : undefined;
  const liveFrame: SceneFrame = {
    ...(teaching
      ? baseFrame
      : {
          kind: 'practice' as const,
          phase: 0,
          progress: 1,
          focus: 'none' as const,
          caption:
            'Every move changes both entire sides. Tap a symbol to ask about it.',
          word: '',
          equation: formatEquation(
            panel === 'check' ? stage.initialEquation : progress.equation,
          ),
        }),
    ...(spokenFocus?.word
      ? {
          focus: spokenFocus.focus,
          pointerFocus: spokenFocus.focus,
          word: spokenFocus.word,
        }
      : {}),
  };
  const boardFocus = spokenFocus?.word
    ? spokenFocus.normalizedToken
    : selectedTerm;
  const narratedMove =
    voice.source?.kind === 'reply' &&
    reply?.provider !== 'prepared-workshop' &&
    mode === 'practice' &&
    panel === 'solve'
      ? narratedOperation(
          progress.equation,
          voice.source.words ?? [],
          voice.position,
        )
      : undefined;
  const tutorState =
    recorder.status === 'recording'
      ? 'Listening to you'
      : tutorBusy
        ? 'Thinking about your question…'
        : audioBusy
          ? 'Preparing my voice…'
          : recorder.status === 'transcribing'
            ? 'Understanding your words…'
            : voice.playing
              ? 'Speaking · you can interrupt'
              : recorder.enabled
                ? 'Mic on · I’m listening'
                : teaching
                  ? 'Here when you need me'
                  : 'Ask me anything about this step';
  function switchBoard(style: BoardStyle) {
    if (notebook.open && notebook.active) {
      setBoardStyle(style);
      savePreference('board', style);
      void playNote(notebook.active);
      return;
    }
    interrupt();
    recorder.cancelTurn();
    voice.clear();
    setBoardStyle(style);
    savePreference('board', style);
    setShowWorking(false);
    setSelectedTerm('');
    markActivity();
    if (teaching) {
      goBeat(beatIndex, true);
      return;
    }
    if (demonstration) {
      void sayGuidance(demonstration.text);
      return;
    }
    void sayGuidance(viewNarration(stage, progress, panel, style));
  }
  function setPracticePanel(next: Panel) {
    notebook.setOpen(false);
    playingPageId.current = undefined;
    interrupt();
    setSpokenProposal(undefined);
    recorder.cancelTurn();
    voice.clear();
    setPanel(next);
    setDemonstrating(false);
    setShowWorking(next === 'check');
    void sayGuidance(viewNarration(stage, progress, next, boardStyle));
    setDemonstration(undefined);
    markActivity();
    setStatus('');
  }
  async function applyOperation(event: FormEvent) {
    event.preventDefault();
    await commitOperation({ kind: operation, value: amount.trim() });
  }
  async function checkCandidate(event: FormEvent) {
    event.preventDefault();
    await submitCandidate();
  }
  const activityKey = `${session.id}:${stageId}:${progress.generation}`;
  const tutorialFirst = workshopAudio?.segments.find(
    (s) => s.id === 'sketch-onboarding-write',
  );
  const tutorialSecond = workshopAudio?.segments.find(
    (s) => s.id === 'sketch-onboarding-preview',
  );
  const tutorialCurrent =
    voice.source?.kind === 'reply'
      ? voice.source.url === tutorialFirst?.url
        ? 'first'
        : voice.source.url === tutorialSecond?.url
          ? 'second'
          : undefined
      : undefined;
  const sketchPad = (
    <SketchPad
      key={activityKey}
      activityKey={activityKey}
      onAcceptedEquation={(text) => void acceptWrittenEquation(text)}
      onTutorialRequested={startSketchTutorial}
      onTutorialDismiss={() => {
        interrupt();
        tutorialSequence.current = undefined;
      }}
      tutorialAudioPositionMs={
        tutorialCurrent
          ? (tutorialCurrent === 'second'
              ? (tutorialFirst?.durationMs ?? 0)
              : 0) + voice.position
          : undefined
      }
      tutorialAudioDurationMs={
        tutorialCurrent
          ? Math.min(
              tutorialFirst?.durationMs ?? 1,
              tutorialFirst?.words.find((w) => /^five[.!?,]?$/i.test(w.text))
                ?.endMs ?? Infinity,
            )
          : undefined
      }
    />
  );
  const paperDemo =
    demonstration ??
    (narratedMove
      ? {
          equation: narratedMove.before,
          operation: narratedMove.operation,
          text: voice.source?.kind === 'reply' ? voice.source.text : '',
        }
      : undefined);
  const demoPlan =
    paperDemo?.operation &&
    voice.source?.kind === 'reply' &&
    paperDemo.text === voice.source.text
      ? buildAlgebraTeachingPlan(
          paperDemo.equation,
          paperDemo.operation,
          voice.source.words ?? [],
          voice.duration,
        )
      : undefined;
  const demoStep = demoPlan?.steps
    .filter((s) => voice.position >= s.startMs)
    .at(-1);
  const demoProgress = demonstrationProgress(
    demoStep,
    voice.position,
    paperDemo?.operation?.kind === 'divide',
    audioBusy,
    reducedMotion,
  );
  const visualEquation = paperDemo?.operation
    ? demoProgress >= 0.8
      ? applyMathOperation(paperDemo.equation, paperDemo.operation)
      : paperDemo.equation
    : panel === 'check'
      ? stage.initialEquation
      : progress.equation;
  const pageSpeech =
    !!notebook.active &&
    playingPageId.current === notebook.active.id &&
    voice.source?.kind === 'reply' &&
    voice.source.text === notebook.active.text;
  const paperWords =
    voice.source?.kind === 'reply' && paperDemo?.text === voice.source.text
      ? voice.source.words
      : undefined;
  const workingRecord = (
    <section className="working-record" aria-label="Your checked work">
      <div className="working-record-heading">
        <span className="eyebrow">Your work so far</span>
        <button
          className="text-button"
          onClick={() => setShowWorking(!showWorking)}
          aria-expanded={showWorking}
        >
          {showWorking ? 'Back to equation view' : 'Open working pad'}
        </button>
      </div>
      <ol>
        <li>
          <strong>{formatEquation(stage.initialEquation)}</strong>
          <span>Original relationship</span>
        </li>
        {progress.history.map((entry, i) => (
          <li key={`move-${i}`}>
            <strong>
              {formatEquation(
                i + 1 < progress.history.length
                  ? progress.history[i + 1].equation
                  : progress.equation,
              )}
            </strong>
            <span>{historyLabel(entry.operation)}</span>
          </li>
        ))}
        {progress.workingEquations?.map((line, i) => (
          <li
            key={`written-${i}`}
            className={line.equivalent ? '' : 'needs-review'}
          >
            <strong>{line.draftEquation}</strong>
            <span>
              {line.equivalent
                ? 'Same solution · explain your method'
                : 'Different solution · revisit this line'}
            </span>
          </li>
        ))}
      </ol>
      {showWorking && (
        <>
          {sketchPad}
          <div className="paper-tutor">
            <TutorAvatar speaking={voice.playing} />
            <span>
              Your earlier work stays here. Explain a step or try your next
              idea.
            </span>
          </div>
        </>
      )}
    </section>
  );
  const liveConversation = (
    <>
      {!conversationVisible && spokenProposal && (
        <div className="inline-proposal quiet-proposal">
          <span>
            Proposed:{' '}
            <b>
              {spokenProposal.kind} {spokenProposal.value}
            </b>{' '}
            on both sides. Say “apply that.”
          </span>
          <button
            className="secondary"
            disabled={locked}
            onClick={() => void commitOperation(spokenProposal)}
          >
            Apply that
          </button>
          <button
            className="text-button"
            onClick={() => setSpokenProposal(undefined)}
          >
            Dismiss
          </button>
        </div>
      )}
      {recorder.error && !conversationVisible && (
        <p role="alert" className="feedback">
          {recorder.error}
        </p>
      )}
      {conversationVisible && (
        <aside
          className="live-conversation"
          aria-label="Conversation beside your board"
        >
          {(reply || tutorBusy || recorder.enabled) && (
            <div className="live-conversation-heading">
              <span className="eyebrow">{tutorState}</span>
              {reply && (
                <button
                  className="text-button"
                  aria-expanded={inlineAnswer}
                  onClick={() => setInlineAnswer(!inlineAnswer)}
                >
                  {inlineAnswer ? 'Hide answer' : 'Show answer'}
                </button>
              )}
            </div>
          )}
          {question && (
            <p className="heard-words">
              <span>You:</span> {question}
            </p>
          )}
          {reply && inlineAnswer && (
            <p className="live-reply" aria-live="polite">
              {reply.text}
            </p>
          )}
          {spokenProposal && (
            <div className="inline-proposal">
              <span>
                Proposed move:{' '}
                <b>
                  {spokenProposal.kind} {spokenProposal.value}
                </b>{' '}
                on both sides.
              </span>
              <button
                className="secondary"
                disabled={locked}
                onClick={() =>
                  void commitOperation(spokenProposal).then((next) => {
                    if (next) {
                      setPanel('solve');
                      setSpokenProposal(undefined);
                      setQuestion('');
                    }
                  })
                }
              >
                Apply that
              </button>
              <button
                className="text-button"
                onClick={() => setSpokenProposal(undefined)}
              >
                Dismiss
              </button>
            </div>
          )}
          {recorder.error && (
            <p role="alert" className="feedback">
              {recorder.error}
            </p>
          )}
          <form
            className="board-question"
            onSubmit={(e) => {
              e.preventDefault();
              void handleUtterance(question);
            }}
          >
            <label className="visually-hidden" htmlFor="board-question">
              Ask or say an equation
            </label>
            <input
              id="board-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={
                micActive
                  ? 'Mic is on. Speak anytime, or type here.'
                  : 'Ask a question, or type “I think the equation should be…”'
              }
              maxLength={1500}
            />
            <button
              className="secondary"
              disabled={!question.trim() || busy > 0}
            >
              {tutorBusy ? 'Thinking…' : 'Send'} <Icon name="arrow" />
            </button>
          </form>
          {voice.source?.kind === 'reply' && (
            <button className="text-button" onClick={resumeTeaching}>
              Return to the explanation
            </button>
          )}
          <div className="conversation-preference">
            <label>
              <input
                type="checkbox"
                checked={alwaysConversation}
                onChange={(e) => {
                  setAlwaysConversation(e.target.checked);
                  savePreference('conversation', String(e.target.checked));
                }}
              />
              Always show conversation and typing
            </label>
            <button
              className="text-button"
              onClick={() => setConversationVisible(false)}
            >
              Hide conversation
            </button>
          </div>
        </aside>
      )}
    </>
  );
  return (
    <div
      className="studio"
      onPointerDownCapture={markActivity}
      onPointerMoveCapture={(e) => {
        if (e.buttons) idleState.current.lastActivity = Date.now();
      }}
      onKeyDownCapture={markActivity}
    >
      <audio {...voice.audioProps} aria-label="AI tutor voice" />
      <a className="skip-link" href="#lesson-main">
        Skip to learning activity
      </a>
      <header className="studio-header">
        <a href="/" className="brand" aria-label="Enactry home">
          <span className="brand-mark">
            <Icon name="spark" />
          </span>
          enactry
        </a>
        <div className="course-label">
          {boot.lesson.title}
          <span>Grade {boot.lesson.grade} · Texas · Concept + application</span>
        </div>
        <div className="header-tools">
          <a
            className="library-link"
            href="/?library=1"
            onClick={() => {
              interrupt();
              void recorder.stop();
            }}
          >
            Workshops
          </a>
          <span className="save-status">
            <i />
            {busy ? 'Saving…' : 'Preview · progress saved'}
          </span>
          <button
            className="quiet"
            aria-label="Learning record"
            onClick={() => {
              interrupt();
              setRecordOpen(true);
            }}
          >
            <Icon name="book" />
            <span>Learning record</span>
          </button>
        </div>
      </header>
      <div className="workshop-navigation">
        <nav className="journey" aria-label="Learning stages">
          {stages.map((id, i) => (
            <button
              key={id}
              aria-current={id === stageId ? 'step' : undefined}
              disabled={locked}
              onClick={() => void changeStage(id)}
            >
              <span className="step-number">
                {session.stages[id].complete ? (
                  <Icon name="check" />
                ) : (
                  `0${i + 1}`
                )}
              </span>
              <span>{names[id]}</span>
              {i < 2 && <Icon name="arrow" />}
            </button>
          ))}
          <span className="journey-note">One idea. Make it yours.</span>
        </nav>
        <WorkshopProgress
          view={progressView}
          onReview={openProgress}
          onFinish={openProgress}
        />
      </div>
      <main
        id="lesson-main"
        className={`learning-viewport mode-${mode}`}
        tabIndex={-1}
      >
        <div className="activity-heading">
          <div>
            <span className="eyebrow">
              {mode === 'welcome'
                ? 'A little guidance. A lot of discovery.'
                : teaching
                  ? `Worked lesson · ${beatIndex + 1} of ${teachingBeats.length} · ${beat.label}`
                  : `${names[stageId]} · ${panel === 'model' ? 'Build a model' : panel === 'solve' ? 'Explore the equation' : 'Check & explain'}`}
            </span>
            <h1>
              {mode === 'welcome'
                ? 'Make the unknown make sense.'
                : teaching
                  ? beat.title
                  : panel === 'model'
                    ? 'Turn the story into a relationship.'
                    : panel === 'check'
                      ? 'Make your reasoning visible.'
                      : isConcept
                        ? 'The same value, two expressions.'
                        : 'What does your model reveal?'}
            </h1>
          </div>
          {mode !== 'welcome' && (
            <button
              className="quiet teach-again"
              disabled={locked}
              onClick={() =>
                teaching ? goBeat(0, true) : void explainCurrent()
              }
            >
              <Icon name="replay" />
              <span>{teaching ? 'Restart lesson' : 'Teach me'}</span>
            </button>
          )}
        </div>
        {mode !== 'welcome' && (
          <div className="teaching-tools" aria-label="Teaching style">
            <span>See it your way</span>
            <button
              className="notebook-open-button"
              aria-expanded={notebook.open}
              onClick={() => {
                const page = notebook.active ?? notebook.pages.at(-1);
                if (notebook.open) closeNotebook();
                else if (page) selectNote(page.id);
                else
                  setStatus(
                    'Ask for a hint or explanation to start your tutor notebook.',
                  );
              }}
            >
              Tutor notebook · {notebook.pages.length}
            </button>
            <button
              aria-pressed={boardStyle === 'illustration'}
              onClick={() => switchBoard('illustration')}
            >
              Tiles & illustration
            </button>
            <button
              aria-pressed={boardStyle === 'written'}
              onClick={() => switchBoard('written')}
            >
              Written steps
            </button>
            {mode === 'practice' && (
              <button
                className="text-button"
                onClick={() => void explainCurrent()}
              >
                {boardStyle === 'written'
                  ? 'Explain these written steps'
                  : 'Explain with tiles'}
              </button>
            )}
          </div>
        )}
        {deletedNote && (
          <div className="notebook-saved-toast" role="status">
            Page deleted.
            <button
              className="text-button"
              onClick={() => {
                notebook.restore(deletedNote);
                setDeletedNote(undefined);
              }}
            >
              Undo delete
            </button>
            <button
              className="text-button"
              onClick={() => setDeletedNote(undefined)}
            >
              Dismiss
            </button>
          </div>
        )}
        {notebook.notice && <p role="status">{notebook.notice}</p>}
        {stageId === 'transfer' && mode === 'practice' && (
          <>
            <div className="problem-actions">
              <span>
                Keep exploring. Say “another problem” whenever you want a fresh
                situation.
              </span>
              <button
                className="quiet"
                disabled={locked}
                onClick={() => void newProblem()}
              >
                Try another problem
              </button>
              <button
                className="quiet"
                disabled={locked}
                onClick={() => {
                  interrupt();
                  voice.clear();
                  setCustomProblem(!customProblem);
                }}
              >
                Create my own
              </button>
            </div>
            {customProblem && (
              <form
                className="custom-problem"
                onSubmit={(e) => {
                  e.preventDefault();
                  void newProblem(true);
                }}
              >
                {(['startingFee', 'hourlyRate', 'total'] as const).map(
                  (key, i) => (
                    <label key={key}>
                      {
                        [story.initialLabel, story.rateLabel, story.totalLabel][
                          i
                        ]
                      }
                      <input
                        type="number"
                        required
                        min={key === 'startingFee' ? 0 : 1}
                        max={
                          key === 'hourlyRate'
                            ? 25
                            : key === 'startingFee'
                              ? 100
                              : 500
                        }
                        step="1"
                        value={problemValues[key]}
                        onChange={(e) =>
                          setProblemValues((v) => ({
                            ...v,
                            [key]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ),
                )}
                <button className="primary" disabled={locked}>
                  Use my problem
                </button>
                <p className="microcopy">
                  Use whole-number quantities with an answer from 1 to 24{' '}
                  {story.unitLabel}.
                </p>
              </form>
            )}
          </>
        )}
        {mode === 'welcome' ? (
          <section className="welcome-stage">
            <div className="welcome-copy">
              <span className="lesson-chip">
                <Icon name="leaf" /> Your AI tutor is here
              </span>
              <h2>
                See it. Understand it.
                <br />
                <em>Try it yourself.</em>
              </h2>
              <p>
                Start with a guided example. I’ll show what the symbols mean,
                explain each move, and connect the idea to a real situation.
              </p>
              <div className="welcome-actions">
                <button
                  className="primary"
                  disabled={locked}
                  onClick={() => goBeat(0, true)}
                >
                  <Icon name="play" /> Teach me the idea <Icon name="arrow" />
                </button>
                {session.teaching?.viewedBeatIds.length ? (
                  <button
                    className="secondary"
                    disabled={locked}
                    onClick={() => goBeat(beatIndex, true)}
                  >
                    Continue explanation
                  </button>
                ) : null}
                <button
                  className="text-button"
                  disabled={locked}
                  onClick={skipTeaching}
                >
                  I’m ready to try <Icon name="arrow" />
                </button>
              </div>
              <span className="microcopy">
                {teachingBeats.length} short parts · your pace · no timer
              </span>
            </div>
            <div className="welcome-art">
              <div className="welcome-orbit" />
              <div className="floating-equation">
                <span>
                  {formatEquation(
                    teachingBeats[0].visual?.originalEquation ??
                      teachingBeats[0].equation,
                  )}
                </span>
              </div>
              <div className="welcome-tutor">
                <TutorAvatar pointing />
              </div>
              <div className="hello-bubble">
                <span>Hello, curious mind.</span>
                <p>Let’s make this click.</p>
              </div>
              <span className="art-label">
                An explanation you can interrupt.
              </span>
            </div>
          </section>
        ) : teaching ? (
          <section
            className={`teaching-stage focus-${beat.focus}`}
            aria-label="Worked example"
          >
            {boardStyle === 'written' ? (
              <WrittenBoard
                playbackId={voice.playbackId}
                authoredPlan={
                  beat.visual
                    ? packageWrittenPlan(beat, sceneSegment)
                    : undefined
                }
                equation={
                  beat.visual?.originalEquation ?? beat.before ?? beat.equation
                }
                candidate={beat.visual?.candidate}
                operation={beat.visual?.operation}
                words={sceneSegment?.words}
                positionMs={lessonPosition}
                durationMs={sceneSegment?.durationMs}
                speaking={voice.playing && voice.source?.kind === 'lesson'}
                reduced={reducedMotion}
              >
                <div className="paper-tutor">
                  <TutorAvatar speaking={voice.playing} />
                  <span>You can pause me or ask about any step.</span>
                </div>
              </WrittenBoard>
            ) : (
              <AnimatedBoard
                frame={liveFrame}
                equation={beat.visual ? packageVisual.equation : undefined}
                operation={packageVisual.operation}
                operationProgress={packageVisual.operationProgress}
                candidate={packageVisual.candidate}
                speaking={voice.playing}
                reduced={reducedMotion}
                onPoint={(term) => {
                  setSelectedTerm(term);
                  void askTutor(
                    undefined,
                    `Explain ${term === 'x' ? 'the unknown x' : term === 'constant' ? 'the added number' : term === 'total' ? 'the right side' : 'the equals sign'} in this example.`,
                  );
                }}
                label={
                  beat.id === 'your-turn'
                    ? 'Your practice equation'
                    : 'Watch the worked example'
                }
              />
            )}
          </section>
        ) : (
          <div className={`practice-stage panel-${panel}`}>
            <div className="practice-scene">
              {notebook.open && notebook.active ? (
                <TutorNotebook
                  page={notebook.active}
                  pages={notebook.pages}
                  style={boardStyle}
                  playing={pageSpeech && voice.playing}
                  busy={audioBusy || tutorBusy}
                  position={pageSpeech ? voice.position : 0}
                  duration={pageSpeech ? voice.duration : 0}
                  words={
                    pageSpeech && voice.source?.kind === 'reply'
                      ? voice.source.words
                      : []
                  }
                  playbackId={voice.playbackId}
                  reduced={reducedMotion}
                  onSelect={selectNote}
                  onPlay={() => void playNote(notebook.active!)}
                  onPause={interrupt}
                  onClose={closeNotebook}
                  onDelete={() => {
                    interrupt();
                    voice.clear();
                    playingPageId.current = undefined;
                    setDeletedNote(notebook.remove(notebook.active!.id));
                    diagnostic('note-delete');
                  }}
                  onAsk={(text) => void askTutor(undefined, text)}
                />
              ) : (
                <>
                  {!(showWorking && panel !== 'model') &&
                    (boardStyle === 'written' &&
                    (panel !== 'model' || demonstrating) ? (
                      <WrittenBoard
                        equation={
                          paperDemo?.equation ??
                          (panel === 'check'
                            ? stage.initialEquation
                            : progress.equation)
                        }
                        candidate={
                          panel === 'check'
                            ? progress.candidateCheck?.value
                            : undefined
                        }
                        operation={paperDemo?.operation}
                        words={paperWords}
                        positionMs={paperWords ? voice.position : 0}
                        durationMs={paperWords ? voice.duration : 0}
                        speaking={voice.playing}
                        reduced={reducedMotion}
                        playbackId={voice.playbackId}
                      >
                        <div className="paper-tutor">
                          <TutorAvatar speaking={voice.playing} />
                          <span>
                            {paperDemo
                              ? 'Watch the explanation. Your own work stays saved.'
                              : 'Ask for an explanation, or work through a step.'}
                          </span>
                          <button
                            className="text-button"
                            onClick={() =>
                              paperDemo
                                ? void sayGuidance(paperDemo.text)
                                : void explainCurrent(true)
                            }
                          >
                            Hear the steps
                          </button>
                        </div>
                        {panel === 'model' && (
                          <button
                            className="secondary return-to-build"
                            onClick={() => {
                              interrupt();
                              voice.clear();
                              setDemonstrating(false);
                              setDemonstration(undefined);
                              setPracticePanel('model');
                            }}
                          >
                            Build my equation
                          </button>
                        )}
                      </WrittenBoard>
                    ) : !isConcept && panel === 'model' && !demonstrating ? (
                      boardStyle === 'written' ? (
                        <section
                          className="working-record written-model"
                          aria-label="Written model workspace"
                        >
                          <div className="working-record-heading">
                            <span className="eyebrow">Your story notes</span>
                          </div>
                          <div className="story-facts">
                            {facts.map((f) => (
                              <span key={f.label}>
                                <strong>{f.display}</strong> {f.label}
                              </span>
                            ))}
                          </div>
                          {sketchPad}
                          <label className="model-draft">
                            Your confirmed model
                            <input
                              aria-label="Your confirmed model"
                              value={draftEquation}
                              onChange={(e) =>
                                setDrafts((d) => ({
                                  ...d,
                                  [stageId]: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <button
                            className="primary"
                            disabled={locked || !draftEquation.trim()}
                            onClick={() => void checkModel()}
                          >
                            Check my model
                          </button>
                          {progress.modelEquation && (
                            <p>{progress.modelEquation.feedback}</p>
                          )}
                        </section>
                      ) : (
                        <EquationWorkshop
                          value={draftEquation}
                          onChange={(value) => {
                            setDrafts((d) => ({ ...d, [stageId]: value }));
                            setStatus('');
                          }}
                          onCheck={() => void checkModel()}
                          transfer={stageId === 'transfer'}
                          stage={stage}
                          prices={[
                            stage.initialEquation.left.constant,
                            stage.initialEquation.left.x,
                            stage.initialEquation.right.constant,
                          ]}
                          onModeChange={() => {
                            interrupt();
                            voice.clear();
                            setDemonstrating(false);
                          }}
                          speaking={voice.playing}
                          focusTerm={boardFocus}
                          onSelect={setSelectedTerm}
                          disabled={locked}
                          sketch={sketchPad}
                          feedback={
                            progress.modelEquation?.draftEquation ===
                            parsedDraft(draftEquation)?.draftEquation
                              ? progress.modelEquation?.feedback
                              : undefined
                          }
                          correct={
                            progress.modelEquation?.correct &&
                            progress.modelEquation.draftEquation ===
                              parsedDraft(draftEquation)?.draftEquation
                          }
                        />
                      )
                    ) : (
                      <AnimatedBoard
                        frame={{
                          ...liveFrame,
                          equation: formatEquation(visualEquation),
                        }}
                        equation={visualEquation}
                        operation={paperDemo?.operation}
                        operationProgress={reducedMotion ? 1 : demoProgress}
                        speaking={voice.playing}
                        reduced={reducedMotion}
                        label={
                          panel === 'check'
                            ? 'Check the original relationship'
                            : 'Your equation workspace'
                        }
                        onPoint={(term) => {
                          setSelectedTerm(term);
                          void askTutor(
                            undefined,
                            `Explain ${term === 'x' ? 'x' : term === 'constant' ? 'the added number' : term === 'total' ? 'the right side' : 'the equals sign'} in my current equation.`,
                          );
                        }}
                      >
                        {paperDemo?.operation ? (
                          <OperationDemonstration
                            before={paperDemo.equation}
                            operation={paperDemo.operation}
                            after={applyMathOperation(
                              paperDemo.equation,
                              paperDemo.operation,
                            )}
                            reduced={reducedMotion}
                            mediaProgress={reducedMotion ? 1 : demoProgress}
                          />
                        ) : narratedMove ? (
                          <div className="narrated-preview">
                            <p>
                              Tutor demonstration · your equation stays
                              unchanged
                            </p>
                            <OperationDemonstration
                              {...narratedMove}
                              reduced={reducedMotion}
                              mediaProgress={
                                reducedMotion ? 1 : narratedMove.progress
                              }
                            />
                          </div>
                        ) : (
                          panel === 'solve' &&
                          progress.history.at(-1) && (
                            <OperationDemonstration
                              key={progress.history.at(-1)!.at}
                              before={progress.history.at(-1)!.equation}
                              operation={progress.history.at(-1)!.operation}
                              after={progress.equation}
                              reduced={reducedMotion}
                              mediaProgress={1}
                            />
                          )
                        )}
                        {panel !== 'check' && progress.history.length > 0 && (
                          <div className="board-history">
                            <span>Both sides changed together.</span>
                            <button
                              className="text-button"
                              disabled={locked}
                              onClick={() => void send({ type: 'undo' })}
                            >
                              Undo last move
                            </button>
                          </div>
                        )}
                      </AnimatedBoard>
                    ))}
                  {(panel === 'check' || showWorking) && workingRecord}
                  {panel === 'solve' && !showWorking && (
                    <button
                      className="quiet open-work-pad"
                      onClick={() => setShowWorking(true)}
                    >
                      Open my working pad & checked steps
                    </button>
                  )}
                  {!isConcept && panel !== 'model' && (
                    <div className="story-recap">
                      <Icon name="route" />
                      <span>
                        {facts
                          .map((f) => `${f.label}: ${f.display}`)
                          .join(' · ')}
                      </span>
                      <button
                        className="text-button"
                        onClick={() => setPracticePanel('model')}
                      >
                        See story
                      </button>
                    </div>
                  )}
                  <div className="practice-companion">
                    <div>
                      <span className="eyebrow">Your AI tutor</span>
                      <p>
                        {progress.complete
                          ? 'You made the relationship make sense.'
                          : panel === 'model'
                            ? 'What changes, and what stays fixed?'
                            : panel === 'check'
                              ? 'A good explanation shows why.'
                              : 'Your ideas drive this. I’m here to help.'}
                      </p>
                    </div>
                    <button
                      className="round"
                      aria-label="Ask your AI tutor"
                      onClick={() => openTray('ask')}
                    >
                      <Icon name="spark" />
                    </button>
                  </div>
                </>
              )}
            </div>
            <section className="task-card" aria-label="Current learning task">
              <nav className="task-tabs" aria-label="Activity views">
                {!isConcept && (
                  <button
                    aria-current={panel === 'model' ? 'page' : undefined}
                    onClick={() => setPracticePanel('model')}
                  >
                    Model
                  </button>
                )}
                <button
                  aria-current={panel === 'solve' ? 'page' : undefined}
                  disabled={!isConcept && !hasModel(progress)}
                  onClick={() => setPracticePanel('solve')}
                >
                  Explore
                </button>
                <button
                  aria-current={panel === 'check' ? 'page' : undefined}
                  disabled={!isConcept && !hasModel(progress)}
                  onClick={() => setPracticePanel('check')}
                >
                  Check & explain
                </button>
              </nav>
              <div className="task-content">
                {panel === 'model' && !demonstrating && (
                  <>
                    <div className="story-illustration">
                      <WorkshopArtwork theme={story.theme} />
                    </div>
                    <p className="story-prompt">{stage.prompt}</p>
                  </>
                )}
                {notebook.open && notebook.active ? (
                  <div className="demo-task">
                    <div id="notebook-controls-slot" />
                    <button
                      className="primary notebook-return"
                      onClick={closeNotebook}
                    >
                      Back to my work
                    </button>
                  </div>
                ) : demonstrating ? (
                  <div className="demo-task">
                    <span className="eyebrow">Tutor demonstration</span>
                    <h2>Watch why this step works.</h2>
                    <p>
                      This is an explanation of{' '}
                      {formatEquation(
                        demonstration?.equation ?? progress.equation,
                      )}
                      . Your saved work does not change while I demonstrate.
                    </p>
                    <p>
                      Follow the{' '}
                      {boardStyle === 'written'
                        ? 'pencil'
                        : 'tiles and pointer'}
                      . Pause or ask why at any time.
                    </p>
                    <button
                      className="primary"
                      onClick={() => {
                        interrupt();
                        voice.clear();
                        setDemonstrating(false);
                        setDemonstration(undefined);
                        setStatus(
                          'Your turn. Continue from your saved equation.',
                        );
                        void sayGuidance(
                          viewNarration(stage, progress, panel, boardStyle),
                        );
                      }}
                    >
                      Your turn
                    </button>
                  </div>
                ) : panel === 'model' ? (
                  <>
                    {!progress.unknownAnswer?.correct ? (
                      <>
                        <span className="eyebrow">Define the unknown</span>
                        <h2>{stage.unknownChoice}</h2>
                        <p>
                          The quantities are known. Choose what the story is
                          asking you to find.
                        </p>
                        <Choices
                          options={stage.unknownOptions}
                          selected={progress.unknownAnswer?.choiceId}
                          disabled={locked}
                          onChoose={(choiceId) =>
                            void send({ type: 'unknownAnswer', choiceId })
                          }
                        />
                        {progress.unknownAnswer &&
                          !progress.unknownAnswer.correct && (
                            <p className="feedback">
                              That quantity is already given. Which one is still
                              unknown?
                            </p>
                          )}
                      </>
                    ) : (
                      <>
                        <span className="eyebrow">
                          Connect words to symbols
                        </span>
                        <h2>
                          {hasModel(progress)
                            ? 'Your model fits the story.'
                            : 'Build the relationship.'}
                        </h2>
                        <p>
                          {hasModel(progress)
                            ? `Now explore this model to find the ${story.unitLabel}. Your checked model stays saved; you can revisit it here.`
                            : `x means ${story.unknownLabel}. Use the whiteboard: ${story.initialLabel} is counted once; ${story.rateLabel} describes the repeated quantity.`}
                        </p>
                        {!hasModel(progress) && (
                          <p className="microcopy">
                            Say “I think the equation should be…” followed by
                            your equation. Then say “check my model.”
                          </p>
                        )}
                        {hasModel(progress) && (
                          <button
                            className="primary"
                            onClick={() => setPracticePanel('solve')}
                          >
                            Explore this model <Icon name="arrow" />
                          </button>
                        )}
                      </>
                    )}
                  </>
                ) : panel === 'solve' ? (
                  <>
                    <span className="eyebrow">
                      {isolated(progress)
                        ? 'Ready to verify'
                        : 'Your practice turn'}
                    </span>
                    <h2>
                      {isolated(progress)
                        ? 'You have isolated x.'
                        : 'Choose your next step.'}
                    </h2>
                    <p className="current-equation">
                      Start from{' '}
                      <strong>{formatEquation(progress.equation)}</strong>
                    </p>
                    <p>
                      {isolated(progress)
                        ? 'Use Check & explain to verify this value in the original relationship.'
                        : 'Your saved work shows where you have been. Choose a new operation from the current equation shown here.'}
                    </p>
                    {!isolated(progress) && (
                      <form
                        onSubmit={applyOperation}
                        className="operation-form"
                      >
                        <label>
                          Operation
                          <select
                            value={operation}
                            onChange={(e) =>
                              setOperation(
                                e.target.value as AlgebraOperation['kind'],
                              )
                            }
                            disabled={locked}
                          >
                            <option value="subtract">Subtract</option>
                            <option value="add">Add</option>
                            <option value="divide">Divide by</option>
                            <option value="multiply">Multiply by</option>
                          </select>
                        </label>
                        <label>
                          Value
                          <input
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="e.g. 2 or 1/2"
                            disabled={locked}
                            autoComplete="off"
                          />
                        </label>
                        <button
                          className="primary"
                          disabled={locked || !amount.trim()}
                        >
                          Apply to both sides <Icon name="arrow" />
                        </button>
                      </form>
                    )}
                    {!isolated(progress) && (
                      <p className="microcopy">
                        Add or subtract a number or an x-term. Divide or
                        multiply by a nonzero number.
                      </p>
                    )}
                    {progress.history.length > 0 && (
                      <div className="last-move">
                        <Icon name="check" />
                        <span>
                          {progress.history.length}{' '}
                          {progress.history.length === 1 ? 'move' : 'moves'}{' '}
                          recorded ·{' '}
                          <button
                            className="text-button"
                            onClick={() => openTray('history')}
                          >
                            See your steps
                          </button>
                        </span>
                      </div>
                    )}
                    <button
                      className="secondary"
                      onClick={() => setPracticePanel('check')}
                    >
                      Ready to check my value <Icon name="arrow" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="eyebrow">Check in the original</span>
                    <h2>What value makes it true?</h2>
                    <form onSubmit={checkCandidate} className="check-form">
                      <label>
                        Value of x
                        <input
                          value={candidate}
                          onChange={(e) => setCandidate(e.target.value)}
                          placeholder="Your value"
                          disabled={locked}
                        />
                      </label>
                      {!isConcept && (
                        <label>
                          Units
                          <select
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            disabled={locked}
                          >
                            <option value="">Choose units</option>
                            {stage.unitOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <button
                        className="primary"
                        disabled={
                          locked || !candidate.trim() || (!isConcept && !unit)
                        }
                      >
                        Check answer <Icon name="check" />
                      </button>
                    </form>
                    {progress.candidateCheck && (
                      <div
                        className={`check-result ${progress.candidateCheck.correct ? 'correct' : ''}`}
                      >
                        <strong>
                          {progress.candidateCheck.correct
                            ? `The substitution works for x = ${progress.candidateCheck.value}.`
                            : `x = ${progress.candidateCheck.value} does not check in the original yet.`}
                        </strong>
                        <span>
                          {substitutionText(
                            stage.initialEquation,
                            progress.candidateCheck.value,
                          )}
                        </span>
                        {!isConcept && !progress.unitAnswer?.correct && (
                          <span>Check the units: what did x stand for?</span>
                        )}
                      </div>
                    )}
                    {progress.candidateCheck?.correct && (
                      <>
                        <h3>Why does your reasoning work?</h3>
                        <Choices
                          options={stage.explanationOptions}
                          selected={progress.explanationAnswer?.choiceId}
                          disabled={locked}
                          onChoose={(choiceId) =>
                            void send({ type: 'explain', choiceId })
                          }
                        />
                        {progress.explanationAnswer &&
                          !progress.explanationAnswer.correct && (
                            <p className="feedback">
                              {isConcept
                                ? 'Consider the entire expression on each side, not just the x-term.'
                                : `Explain what the number means, with ${story.unitLabel}, and check it against every quantity in the story.`}
                            </p>
                          )}
                        {!progress.complete && (
                          <p className="microcopy">
                            Show your equation steps in Explore or the working
                            pad, check the original, and choose a valid
                            explanation
                            {!isConcept ? ' with the correct units' : ''}.
                          </p>
                        )}
                      </>
                    )}
                    {progress.complete && (
                      <div className="completion">
                        <Icon name="check" />
                        <strong>This activity is complete.</strong>
                        <p>
                          Your next step uses the idea in a different situation.
                        </p>
                        {nextStage ? (
                          <button
                            className="primary"
                            disabled={locked}
                            onClick={() => void changeStage(nextStage)}
                          >
                            Continue to {names[nextStage]} <Icon name="arrow" />
                          </button>
                        ) : (
                          <button
                            className="primary"
                            onClick={() => {
                              interrupt();
                              setRecordOpen(true);
                            }}
                          >
                            See my learning record <Icon name="book" />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {liveConversation}
            </section>
          </div>
        )}
        {teaching && liveConversation}
      </main>
      <footer className="lesson-footer">
        {teaching ? (
          <div className="teaching-navigation">
            <button
              className="quiet"
              disabled={beatIndex === 0 || locked}
              onClick={() => goBeat(beatIndex - 1, voice.playing)}
            >
              <span>←</span>
              <span>Back</span>
            </button>
            <div className="beat-dots" aria-label="Explanation parts">
              {teachingBeats.map((b, i) => (
                <button
                  key={b.id}
                  aria-label={`Part ${i + 1}: ${b.label}`}
                  aria-current={i === beatIndex ? 'step' : undefined}
                  disabled={locked}
                  onClick={() => goBeat(i, voice.playing)}
                >
                  <span />
                </button>
              ))}
            </div>
            <button
              className="primary"
              disabled={locked}
              onClick={() =>
                beatIndex < teachingBeats.length - 1
                  ? goBeat(beatIndex + 1, voice.playing)
                  : void finishTeaching(true)
              }
            >
              {beatIndex < teachingBeats.length - 1
                ? 'Next part'
                : 'Try it myself'}
              <Icon name="arrow" />
            </button>
            <button
              className="text-button skip-explanation"
              disabled={locked}
              onClick={skipTeaching}
            >
              Go to practice
            </button>
          </div>
        ) : mode === 'practice' ? (
          <div className="practice-footer">
            <span>Texas grade {boot.lesson.grade} · two-step equations</span>
            <button
              className="text-button"
              disabled={locked}
              onClick={() => {
                void send({ type: 'restartStage' }).then((next) => {
                  if (next) {
                    setPanel(isConcept ? 'solve' : 'model');
                    setDrafts((d) => ({ ...d, [stageId]: '' }));
                    setCandidate('');
                    setUnit('');
                    setAmount('');
                  }
                });
              }}
            >
              <Icon name="replay" /> Reset this activity
            </button>
          </div>
        ) : (
          <div className="practice-footer">
            <span>Understand the concept. Use it in the world.</span>
            <span>Internal learning preview</span>
          </div>
        )}
        <div className="tutor-dock">
          <button
            className="dock-identity"
            onClick={() => openTray('ask')}
            aria-label="Open AI tutor"
          >
            <span>
              <strong>
                Enactry <span>AI tutor</span>
              </strong>
              <small>{tutorState}</small>
            </span>
          </button>
          <div className="dock-caption" aria-live="off">
            {tutorBusy || audioBusy || recorder.status === 'transcribing'
              ? `${tutorState} ${recorder.enabled ? 'Keep speaking if you want to add something.' : 'Your work stays available while I prepare.'}`
              : helpOffered
                ? 'A hint, another explanation, or more thinking time?'
                : voice.playing && voice.source?.kind === 'reply'
                  ? voice.source.text
                  : teaching
                    ? currentSentence || beat.takeaway
                    : status ||
                      'You can ask, listen, or explore at your own pace.'}
          </div>
          <div className="dock-controls">
            {voice.source && (
              <button
                className="round"
                aria-label={voice.playing ? 'Pause tutor' : 'Play tutor'}
                onClick={() => (voice.playing ? interrupt() : resumeCurrent())}
              >
                <Icon name={voice.playing ? 'pause' : 'play'} />
              </button>
            )}
            <button
              className={`round mic-button ${micActive ? 'active' : ''}`}
              aria-label={
                micActive ? 'Stop microphone' : 'Speak to interrupt the tutor'
              }
              disabled={!secureMic || !boot.capabilities?.stt.available}
              onClick={() =>
                micActive ? void recorder.stop() : void recorder.start()
              }
              title={
                !secureMic
                  ? 'Remote microphone needs HTTPS. Typed questions work here.'
                  : 'Enable microphone; speaking pauses the tutor'
              }
            >
              <Icon name={micActive ? 'stop' : 'mic'} />
            </button>
            <button
              className="quiet ask-button"
              aria-expanded={conversationVisible}
              onClick={() => {
                setConversationVisible(!conversationVisible);
                markActivity();
              }}
            >
              <Icon name="spark" />{' '}
              {conversationVisible ? 'Hide conversation' : 'Conversation'}
            </button>
            <button
              className="round"
              aria-label="Tutor voice and playback settings"
              onClick={() => openTray('settings')}
            >
              <Icon name="settings" />
            </button>
          </div>
        </div>
      </footer>
      <dialog
        ref={trayRef}
        className="tutor-tray"
        onCancel={() => {
          interrupt();
          setTrayOpen(false);
        }}
        onClose={() => setTrayOpen(false)}
      >
        <div className="panel-heading">
          <div className="tray-identity">
            <div>
              <span className="eyebrow">Enactry · AI tutor</span>
              <h2>
                {tray === 'ask'
                  ? 'Let’s think it through.'
                  : tray === 'transcript'
                    ? 'Follow the explanation.'
                    : tray === 'history'
                      ? 'Your reasoning, step by step.'
                      : 'Make this space yours.'}
              </h2>
            </div>
          </div>
          <button
            className="round"
            aria-label="Close tutor panel"
            onClick={() => {
              interrupt();
              setTrayOpen(false);
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        <nav className="tray-tabs" aria-label="Tutor tools">
          {(['ask', 'transcript', 'history', 'settings'] as Tray[]).map((t) => (
            <button
              key={t}
              aria-current={tray === t ? 'page' : undefined}
              onClick={() => setTray(t)}
            >
              {t === 'ask'
                ? 'Conversation'
                : t === 'transcript'
                  ? 'Transcript'
                  : t === 'history'
                    ? 'Your steps'
                    : 'Voice & controls'}
            </button>
          ))}
        </nav>
        <div className="tray-content">
          {tray === 'ask' ? (
            <>
              <p className="context-line">
                {teaching
                  ? `We’re discussing: ${beat.title} ${formatEquation(beat.equation)}`
                  : `We’re discussing: ${formatEquation(panel === 'check' ? stage.initialEquation : progress.equation)}`}
              </p>
              <p className="microcopy preview-disclosure">
                Preview: use fictional questions. The HTTPS studio keeps records
                private to this browser. Voice runs on the Enactry server;
                family accounts and parent consent are still being developed.
              </p>
              <form onSubmit={askTutor}>
                <label htmlFor="tutor-question">Your question or idea</label>
                <textarea
                  id="tutor-question"
                  ref={questionRef}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Why do we change both sides?"
                  rows={3}
                />
                <div className="conversation-actions">
                  <button
                    type="button"
                    className="secondary"
                    disabled={!secureMic || !boot.capabilities?.stt.available}
                    onClick={() =>
                      micActive ? void recorder.stop() : void recorder.start()
                    }
                  >
                    <Icon name={micActive ? 'stop' : 'mic'} />
                    {micActive ? 'Turn mic off' : 'Turn mic on'}
                  </button>
                  <button
                    className="primary"
                    disabled={locked || !question.trim()}
                  >
                    {tutorBusy ? 'Thinking…' : 'Ask tutor'}
                    <Icon name="arrow" />
                  </button>
                </div>
              </form>
              {!secureMic && (
                <p className="note">
                  Microphone access requires HTTPS on a remote device. You can
                  type here or{' '}
                  <a
                    href="https://enactry.app.gacud.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    open the HTTPS studio
                  </a>
                  , which has a separate saved session.
                </p>
              )}
              {recorder.status !== 'idle' && (
                <p role="status" className="feedback">
                  {tutorState}{' '}
                  {micActive && (
                    <button
                      className="text-button"
                      onClick={() => void recorder.stop()}
                    >
                      Turn mic off
                    </button>
                  )}
                </p>
              )}
              {recorder.error && (
                <p role="alert" className="feedback">
                  {recorder.error}
                </p>
              )}
              {proposal && mode === 'practice' && (
                <div className="proposal">
                  <strong>Confirm this move</strong>
                  <p>
                    {proposal.kind} {proposal.value} on both entire sides.
                  </p>
                  <button
                    className="secondary"
                    disabled={locked}
                    onClick={() =>
                      void commitOperation(proposal).then((next) => {
                        if (next) {
                          setPanel('solve');
                          setTrayOpen(false);
                          setQuestion('');
                        }
                      })
                    }
                  >
                    Apply this operation
                  </button>
                </div>
              )}
              {reply && (
                <section className="tutor-reply" aria-label="Tutor answer">
                  <span className="eyebrow">
                    {reply.provider.toLowerCase().includes('fallback')
                      ? 'Prepared guidance'
                      : 'Answer to your question'}
                  </span>
                  <p>{reply.text}</p>
                  <button
                    className="text-button"
                    disabled={audioBusy || !boot.capabilities?.tts.available}
                    onClick={() =>
                      voice.playing ? interrupt() : void speakReply(reply.text)
                    }
                  >
                    <Icon name={voice.playing ? 'pause' : 'sound'} />
                    {voice.playing
                      ? 'Pause answer'
                      : audioBusy
                        ? 'Preparing voice…'
                        : 'Hear this answer'}
                  </button>
                </section>
              )}
              <div className="hint-inline">
                <button
                  className="text-button"
                  disabled={locked}
                  onClick={() => {
                    setTrayOpen(false);
                    void requestHint();
                  }}
                >
                  <Icon name="leaf" /> Get a hint for my activity
                </button>
                {progress.hintsUsed > 0 && (
                  <p>
                    {
                      stage.hints[
                        Math.min(progress.hintsUsed, stage.hints.length) - 1
                      ]
                    }
                  </p>
                )}
              </div>
              <p className="microcopy">
                Questions use your hosted tutor. Voice is processed by the
                Enactry server. AI guidance can be mistaken; equation checks use
                exact rules.
              </p>
            </>
          ) : tray === 'transcript' ? (
            <>
              <span className="lesson-chip">
                {voice.source?.kind === 'reply'
                  ? 'Current tutor answer'
                  : 'Prepared lesson · reusable narration'}
              </span>
              <p className="transcript">
                {currentSegment &&
                voice.source?.kind === 'lesson' &&
                currentSegment.words.length
                  ? currentSegment.words.map((w, i) => (
                      <span
                        key={i}
                        className={
                          voice.position >= w.startMs &&
                          voice.position < w.endMs
                            ? 'spoken-word'
                            : ''
                        }
                      >
                        {w.text}{' '}
                      </span>
                    ))
                  : transcript}
              </p>
              {currentSegment && (
                <>
                  <label htmlFor="voice-position">
                    Narration position · {clock(voice.position)} /{' '}
                    {clock(currentSegment.durationMs)}
                  </label>
                  <input
                    id="voice-position"
                    type="range"
                    min={0}
                    max={currentSegment.durationMs}
                    step={100}
                    value={Math.min(voice.position, currentSegment.durationMs)}
                    onChange={(e) => voice.seek(Number(e.target.value))}
                  />
                  <div className="conversation-actions">
                    <button
                      className="secondary"
                      onClick={() =>
                        voice.playing ? interrupt() : resumeCurrent()
                      }
                    >
                      <Icon name={voice.playing ? 'pause' : 'play'} />
                      {voice.playing ? 'Pause tutor' : 'Play tutor'}
                    </button>
                    <button className="text-button" onClick={replayVoice}>
                      <Icon name="replay" /> Replay this part
                    </button>
                  </div>
                </>
              )}
            </>
          ) : tray === 'history' ? (
            <>
              <p>
                Every operation applies to both entire sides. Undo returns to
                the previous equation.
              </p>
              {progress.history.length ? (
                <ol className="history-list">
                  {progress.history.map((entry, i) => (
                    <li key={i}>
                      <strong>{formatEquation(entry.equation)}</strong>
                      <span>
                        {entry.operation.kind} {entry.operation.value} on both
                        sides
                      </span>
                    </li>
                  ))}
                  <li>
                    <strong>{formatEquation(progress.equation)}</strong>
                    <span>Current equation</span>
                  </li>
                </ol>
              ) : (
                <p className="note">
                  Your first move will appear here. Try an operation in Explore.
                </p>
              )}
            </>
          ) : (
            <>
              <label className="setting-row">
                <span>
                  <strong>Voice for new explanations</strong>
                  <small>
                    Prepared lessons keep their recorded voice. New answers use
                    your choice.
                  </small>
                </span>
                <select
                  aria-label="Voice for new explanations"
                  value={selectedVoice}
                  onChange={(e) => {
                    setSelectedVoice(e.target.value);
                    savePreference('voice', e.target.value);
                    interrupt();
                  }}
                >
                  <option value="af_sarah">Sarah · clear and steady</option>
                  <option value="af_bella">Bella · warm and bright</option>
                </select>
              </label>
              <label className="setting-row">
                <span>
                  <strong>Gentle check-ins</strong>
                  <small>
                    Offer help once after quiet time while the mic is on. Say
                    “give me two minutes” to wait longer.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={checkIns}
                  onChange={(e) => {
                    setCheckIns(e.target.checked);
                    savePreference('check-ins', String(e.target.checked));
                    markActivity();
                  }}
                />
              </label>
              <label className="setting-row">
                <span>
                  <strong>Thinking time before an offer</strong>
                  <small>
                    Drawing, typing and other activity restart the quiet period.
                  </small>
                </span>
                <select
                  aria-label="Thinking time before an offer"
                  value={checkInSeconds}
                  onChange={(e) => {
                    setCheckInSeconds(Number(e.target.value));
                    savePreference('check-in-seconds', e.target.value);
                    markActivity();
                  }}
                >
                  <option value={60}>1 minute</option>
                  <option value={120}>2 minutes</option>
                  <option value={300}>5 minutes</option>
                </select>
              </label>
              <label className="setting-row">
                <span>
                  <strong>Always show conversation and typing</strong>
                  <small>
                    Keep the optional conversation beside your board.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={alwaysConversation}
                  onChange={(e) => {
                    setAlwaysConversation(e.target.checked);
                    setConversationVisible(e.target.checked);
                    savePreference('conversation', String(e.target.checked));
                  }}
                />
              </label>
              <label className="setting-row">
                <span>
                  <strong>Tutor speaking pace</strong>
                  <small>
                    Choose a pace that gives you time to follow the reasoning.
                  </small>
                </span>
                <select
                  aria-label="Tutor speaking pace"
                  value={voice.rate}
                  onChange={(e) => voice.setRate(Number(e.target.value))}
                >
                  <option value={0.75}>Slower · 0.75×</option>
                  <option value={0.9}>Steady · 0.9×</option>
                  <option value={1}>Original · 1×</option>
                  <option value={1.15}>Quicker · 1.15×</option>
                </select>
              </label>
              <label className="setting-row">
                <span>
                  <strong>Advance the explanation with the tutor</strong>
                  <small>
                    After each recorded part finishes, move to the next. You can
                    always pause or go back.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={(e) => setAutoAdvance(e.target.checked)}
                />
              </label>
              <label className="setting-row">
                <span>
                  <strong>Speak tutor replies</strong>
                  <small>
                    Prepare and play a voice answer after your question. Text
                    remains available.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={voiceReplies}
                  onChange={(e) => {
                    setVoiceReplies(e.target.checked);
                    if (!e.target.checked) interrupt();
                  }}
                />
              </label>
              <p className="note">
                The same tutor delivers prepared lessons and live answers.
                Lesson recordings are reused; transcripts and their playback
                position keep questions in context.
              </p>
              <p>
                Turn the microphone on once to speak anytime. It stays on across
                questions and activity views until you turn it off or leave this
                workshop. Speech pauses the tutor immediately. Questions longer
                than 30 seconds need a shorter retry; the mic stays on.
                Headphones help prevent the tutor’s voice from being picked up.
              </p>
              <p className="microcopy">
                Reduced motion follows your device setting. The tutor’s position
                still indicates the part being explained.
              </p>
              <p>
                Recent speech, response, and playback timings are kept on this
                browser for troubleshooting. This diagnostic report contains no
                conversation text or microphone recordings. Saved learning
                records contain tutor questions, answers, and activity progress.
              </p>
              <button className="secondary" onClick={downloadDiagnostics}>
                Download troubleshooting report
              </button>
            </>
          )}
        </div>
        {error && (
          <p className="feedback" role="alert">
            {error}
          </p>
        )}
        {voice.error && (
          <p className="feedback" role="status">
            {voice.error}
          </p>
        )}
        {(teaching || currentSegment) && (
          <div className="tray-footer">
            <button
              className="primary"
              onClick={() => {
                interrupt();
                setTrayOpen(false);
                voice.resumeLesson();
              }}
            >
              Continue explanation <Icon name="play" />
            </button>
            <button
              className="text-button"
              onClick={() => {
                interrupt();
                setTrayOpen(false);
              }}
            >
              Return quietly
            </button>
          </div>
        )}
      </dialog>
      {(error || (voice.error && !trayOpen)) && (
        <div
          className={`notice ${error ? 'error' : ''}`}
          role={error ? 'alert' : 'status'}
        >
          <Icon name={error ? 'book' : 'check'} />
          <span>{error || voice.error || status}</span>
          <button
            className="round"
            aria-label="Dismiss message"
            onClick={() => {
              setError('');
              setStatus('');
              voice.clearError();
            }}
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      <WorkshopReview
        view={progressView}
        open={progressOpen}
        busy={busy > 0}
        error={error}
        onClose={() => setProgressOpen(false)}
        onFinish={() => void leaveWorkshop(true)}
        onExit={() => void leaveWorkshop(false)}
        onContinue={(id) => {
          setProgressOpen(false);
          void changeStage(id);
        }}
      />
      <LearningRecord
        lesson={boot.lesson}
        session={session}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />
    </div>
  );
}
